import React, { useEffect, useState, useRef } from "react";
import { launchCheckout } from "../lib/upsell";
import { PRODUCTS } from "../products";
import PayPalButton from "./PayPalButton";
import { PillCTA, Highlight, Scribble, GlassArt, COBALT, PURPLE, PURPLE_ACCENT } from "./Maca";

const BG = "#FAF7EF";
const CREAM = "#141414";
const GOLD = "#2F2BEB";
const BLUE = "#2F2BEB";
const LIME = "#D9F154";
const BG_ALT = "#FFFFFF";
const BG_ELEV = "#FFFFFF";

// ============================================================================
// 1. VSL PLACEHOLDER — 16:9 video slot with pulsing play button
// ============================================================================
export const WELCOME_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_raise-like-insider/artifacts/st688rk9_Bienvenidos%20a%20The%20Capital%20Guy%20v1.mp4";

export const VSLPlaceholder = ({ tag, title, sub, runtime = "1:32", videoSrc = WELCOME_VIDEO, testid = "vsl" }) => {
  const [playing, setPlaying] = useState(false);
  const play = () => {
    if (videoSrc) setPlaying(true);
    else alert("Video coming soon — Ernesto is recording.");
  };
  return (
    <div data-testid={testid} style={{ position: "relative" }}>
      {tag && (
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }} data-testid={`${testid}-tag`}>
          {tag}
        </div>
      )}
      {title && (
        <h2 className="serif" style={{ fontSize: "clamp(28px, 3.4vw, 46px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.08, margin: 0, maxWidth: 900 }} data-testid={`${testid}-title`}>
          {title}
        </h2>
      )}
      {sub && (
        <p className="serif" style={{ marginTop: 14, fontSize: 17, lineHeight: 1.55, opacity: 0.75, fontStyle: "normal", maxWidth: 620 }} data-testid={`${testid}-sub`}>
          {sub}
        </p>
      )}
      <div
        className="vsl-frame"
        data-testid={`${testid}-frame`}
        onClick={playing ? undefined : play}
        role={playing ? undefined : "button"}
        style={{
          marginTop: 32, width: "100%", aspectRatio: "16 / 9", position: "relative",
          background: playing ? "#000" : `radial-gradient(circle at 30% 20%, rgba(15,56,44,0.10), transparent 55%), linear-gradient(140deg, ${BG_ALT}, ${BG})`,
          border: `1px solid ${GOLD}33`, cursor: playing ? "default" : "pointer", overflow: "hidden",
          padding: 0, color: CREAM, textAlign: "left",
        }}
      >
        {playing ? (
          <video
            data-testid={`${testid}-video`}
            src={videoSrc}
            controls
            autoPlay
            playsInline
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000", display: "block" }}
          />
        ) : (
          <>
            {/* Grid grain background */}
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage: `linear-gradient(rgba(15,56,44,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(15,56,44,0.03) 1px, transparent 1px)`,
              backgroundSize: "40px 40px", opacity: 0.7,
            }} />
            {/* Corner brackets */}
            <span style={{ position: "absolute", top: 20, left: 20, width: 18, height: 18, borderTop: `1.5px solid ${GOLD}`, borderLeft: `1.5px solid ${GOLD}` }} />
            <span style={{ position: "absolute", top: 20, right: 20, width: 18, height: 18, borderTop: `1.5px solid ${GOLD}`, borderRight: `1.5px solid ${GOLD}` }} />
            <span style={{ position: "absolute", bottom: 20, left: 20, width: 18, height: 18, borderBottom: `1.5px solid ${GOLD}`, borderLeft: `1.5px solid ${GOLD}` }} />
            <span style={{ position: "absolute", bottom: 20, right: 20, width: 18, height: 18, borderBottom: `1.5px solid ${GOLD}`, borderRight: `1.5px solid ${GOLD}` }} />
            {/* Play pulse */}
            <div className="vsl-play" style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              width: 96, height: 96, borderRadius: "50%",
              background: GOLD, display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 0 0 12px ${GOLD}22, 0 0 60px ${GOLD}44`,
              animation: "vsl-pulse 2s ease-in-out infinite",
            }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill={BG}>
                <path d="M 8 4 L 20 12 L 8 20 Z" />
              </svg>
            </div>
            {/* Runtime chip */}
            <div className="mono" style={{
              position: "absolute", bottom: 22, right: 22, padding: "6px 12px",
              background: `${BG}dd`, border: `1px solid ${GOLD}55`, color: CREAM,
              fontSize: 11, letterSpacing: "0.18em",
            }}>
              {runtime}
            </div>
            {/* Corner caption */}
            <div className="mono" style={{
              position: "absolute", bottom: 22, left: 22, fontSize: 10,
              letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.75,
            }}>
              Ernesto Gómez Berjón
            </div>
          </>
        )}
        <style>{`
          @keyframes vsl-pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 0 12px ${GOLD}22, 0 0 60px ${GOLD}44; }
            50%      { transform: translate(-50%, -50%) scale(1.05); box-shadow: 0 0 0 24px ${GOLD}00, 0 0 90px ${GOLD}66; }
          }
          .vsl-frame:hover .vsl-play { transform: translate(-50%, -50%) scale(1.08); }
        `}</style>
      </div>
    </div>
  );
};

// ============================================================================
// 2. FEATURED IN — press logo strip
// ============================================================================
const PRESS = ["Bloomberg Línea", "Forbes México", "Expansión", "El Financiero", "LinkedIn News", "Substack Top 100"];

export const FeaturedInStrip = ({ testid = "featured-in" }) => (
  <section
    data-testid={testid}
    style={{ padding: "40px 40px 20px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }}
  >
    <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.32em", color: CREAM, opacity: 0.4, textTransform: "uppercase", textAlign: "center", marginBottom: 22 }}>
      Han hablado de mí
    </div>
    <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "24px 44px" }}>
      {PRESS.map((p) => (
        <span
          key={p}
          data-testid={`${testid}-${p.toLowerCase().replace(/\s/g, "-")}`}
          style={{
            fontFamily: "'Archivo', sans-serif",
            fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase",
            color: CREAM, opacity: 0.55,
            padding: "10px 4px",
          }}
        >
          {p}
        </span>
      ))}
    </div>
  </section>
);

// ============================================================================
// 3. GUARANTEE BADGE — animated circle stamp
// ============================================================================
export const GuaranteeBadge = ({ title = "14 días", subtitle = "garantía", caption = "Sin preguntas", size = 130, testid = "guarantee" }) => (
  <div
    data-testid={testid}
    style={{
      position: "relative", width: size, height: size, display: "flex",
      alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}
  >
    <svg
      width={size} height={size} viewBox="0 0 200 200"
      style={{ position: "absolute", inset: 0, animation: "spin-badge 26s linear infinite" }}
    >
      <defs>
        <path id={`${testid}-arc`} d="M 100,100 m -90,0 a 90,90 0 1,1 180,0 a 90,90 0 1,1 -180,0" />
      </defs>
      <text fill="#141414" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
        <textPath href={`#${testid}-arc`}>· {caption} · sin letra chica · {caption} · sin letra chica </textPath>
      </text>
    </svg>
    <div style={{
      width: size * 0.66, height: size * 0.66, borderRadius: "50%",
      border: "3px solid #141414", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", background: LIME,
      boxShadow: "5px 5px 0 #141414", transform: "rotate(-6deg)",
    }}>
      <div className="serif" style={{ fontSize: size * 0.2, fontWeight: 400, color: "#141414", letterSpacing: "-0.02em", lineHeight: 0.95 }}>
        {title}
      </div>
      <div className="mono" style={{ fontSize: size * 0.078, letterSpacing: "0.22em", textTransform: "uppercase", color: "#141414", fontWeight: 800, marginTop: 5, borderTop: "2px solid #141414", paddingTop: 5 }}>
        {subtitle}
      </div>
    </div>
    <style>{`
      @keyframes spin-badge { from { transform: rotate(0); } to { transform: rotate(360deg); } }
    `}</style>
  </div>
);


// ============================================================================
// 4. TESTIMONIALS BLOCK — 6 case-study cards
// ============================================================================
const TESTIMONIALS = [
  {
    q: "Ernesto nos estructuró nuestro primer warehouse de $12M en 9 semanas. Cero equity regalado. Cerramos con 2 contraofertas sobre la mesa.",
    name: "Fernanda R.",
    role: "CFO",
    company: "Plataforma de crédito SaaS · Serie A",
    result: "Facilidad de $12M · 9 semanas",
  },
  {
    q: "Teníamos dos term sheets que nos iban a costar 4% de dilución. Ernesto reescribió los covenants y lo bajó a 0.8%. No tiene precio.",
    name: "Diego M.",
    role: "Fundador & CEO",
    company: "Fintech de consumo · Seed",
    result: "$25M · 3.2% de dilución ahorrada",
  },
  {
    q: "Como fondo, el pipeline que Ernesto pasa ya viene analizado. Mi equipo se ahorró 40+ horas por deal. Primer cierre en 3 semanas.",
    name: "Sofía A.",
    role: "Managing Director",
    company: "Family office · $600M AUM",
    result: "3 cierres · 60d de ciclo",
  },
  {
    q: "Me había sobrediluido con un SAFE malo. Ernesto reestructuró mi cap table antes de la Serie A y recuperé 6% de mi empresa.",
    name: "Carlos T.",
    role: "Fundador",
    company: "SaaS vertical · Pre-Serie A",
    result: "6% de equity recuperado",
  },
  {
    q: "Para una PyME pensábamos que la deuda era imposible. Ernesto nos consiguió una línea de MXN 30M a tasa suave. Duplicamos la fábrica.",
    name: "Alejandra V.",
    role: "CEO",
    company: "PyME manufacturera · CDMX",
    result: "Línea MXN 30M · 2x capacidad",
  },
  {
    q: "Esto es el único lugar donde los que enseñan de verdad operan. No es un curso, es un cuarto de acceso privado a deals reales.",
    name: "Miguel P.",
    role: "Head of Capital Markets",
    company: "SOFOM institucional · Andina",
    result: "6 deals originados en el grupo",
  },
];

export const TestimonialsBlock = ({ testid = "testimonials" }) => (
  <section
    data-testid={testid}
    style={{ padding: "100px 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }}
  >
    <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
      — LO QUE DICE LA GENTE
    </div>
    <h2 className="serif" style={{ fontSize: "clamp(30px, 3.6vw, 52px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.05, margin: 0, maxWidth: 900 }}>
      Term sheets reales. <span style={{ fontStyle: "normal", color: GOLD }}>Números reales.</span> Fundadores reales.
    </h2>
    <div style={{ marginTop: 60, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 24 }}>
      {TESTIMONIALS.map((t, i) => (
        <article
          key={i}
          data-testid={`${testid}-card-${i}`}
          className="testimonial-card"
          style={{
            background: BG_ALT, border: "2px solid #141414", borderRadius: 16, boxShadow: "4px 4px 0 #141414",
            padding: 32, position: "relative", transition: "border-color 0.3s ease, transform 0.3s ease",
          }}
        >
          <div className="serif" style={{ fontSize: 44, color: GOLD, opacity: 0.35, lineHeight: 1, position: "absolute", top: 18, right: 26, fontStyle: "normal" }}>"</div>
          <p className="serif" style={{ fontSize: 17, lineHeight: 1.55, color: CREAM, fontStyle: "normal", margin: 0, minHeight: 130 }}>
            {t.q}
          </p>
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid rgba(15,56,44,0.08)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: "50%",
                background: `linear-gradient(135deg, ${GOLD}, #165B46)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Archivo', sans-serif", fontWeight: 600, color: BG, fontSize: 15,
              }}>
                {t.name.split(" ").map((s) => s[0]).join("")}
              </div>
              <div>
                <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 14, fontWeight: 600, color: CREAM }}>{t.name}</div>
                <div className="mono" style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: CREAM, opacity: 0.55, marginTop: 2 }}>
                  {t.role} · {t.company}
                </div>
              </div>
            </div>
            <div className="mono" style={{ marginTop: 14, fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD, background: `${GOLD}12`, padding: "6px 10px", display: "inline-block" }}>
              → {t.result}
            </div>
          </div>
        </article>
      ))}
    </div>
    <style>{`
      .testimonial-card:hover { border-color: ${GOLD}66 !important; transform: translateY(-2px); }
    `}</style>
  </section>
);


// ============================================================================
// 5. COMPARISON TABLE — vs alternatives
// ============================================================================
const COMPARISON = {
  cols: [
    { key: "tcg", label: "The Debt Guy", accent: true },
    { key: "yc", label: "Aceleradoras top" },
    { key: "cfo", label: "Contratar un CFO" },
    { key: "diy", label: "Hacerlo solo" },
  ],
  rows: [
    { label: "Equity que te quitan",       tcg: "0-1.5%",              yc: "7-10%",           cfo: "0%",                  diy: "0%" },
    { label: "Revisión de term sheets",    tcg: "Cada semana · contigo", yc: "Casi nunca",    cfo: "Según el caso",       diy: "Tú solo" },
    { label: "Foco en estructurar deuda",  tcg: "100% especializado",  yc: "Generalista",     cfo: "Finanzas en general", diy: "Lo aprendes tú" },
    { label: "Red de prestamistas",        tcg: "+60 curados",         yc: "Casi solo VCs",   cfo: "Su agenda",           diy: "Puerta fría" },
    { label: "Tiempo al primer cierre",    tcg: "6-10 semanas",        yc: "12+ meses",       cfo: "3-6 meses",           diy: "6-12 meses" },
    { label: "Costo",                      tcg: "$199-$7.5k",          yc: "7-10% de equity", cfo: "$15k/mes+",           diy: "Tiempo perdido" },
  ],
};

export const ComparisonTable = ({ testid = "comparison" }) => (
  <section
    data-testid={testid}
    style={{ padding: "100px 40px", background: BG_ALT, position: "relative", zIndex: 5 }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
        — CÓMO SE COMPARA
      </div>
      <h2 className="serif" style={{ fontSize: "clamp(30px, 3.6vw, 52px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.05, margin: 0, maxWidth: 900 }}>
        Ni aceleradora ni CFO. <span style={{ fontStyle: "normal", color: GOLD }}>Un insider.</span>
      </h2>
      <div className="cmp-wrap" style={{ marginTop: 50, overflow: "auto" }}>
        <table
          data-testid={`${testid}-table`}
          style={{
            width: "100%", minWidth: 720, borderCollapse: "collapse",
            fontFamily: "'Archivo', sans-serif", color: CREAM,
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "18px 20px", textAlign: "left", borderBottom: `1px solid ${CREAM}18`, fontWeight: 400 }}></th>
              {COMPARISON.cols.map((c) => (
                <th
                  key={c.key}
                  data-testid={`${testid}-col-${c.key}`}
                  style={{
                    padding: "18px 20px", textAlign: "left",
                    borderBottom: c.accent ? `2px solid ${GOLD}` : `1px solid ${CREAM}18`,
                    background: c.accent ? `${GOLD}0d` : "transparent",
                    fontFamily: "'Archivo', sans-serif", fontSize: 10.5,
                    letterSpacing: "0.2em", textTransform: "uppercase",
                    color: c.accent ? GOLD : CREAM, opacity: c.accent ? 1 : 0.65, fontWeight: 500,
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON.rows.map((r, i) => (
              <tr key={i} data-testid={`${testid}-row-${i}`}>
                <td style={{
                  padding: "20px", borderBottom: `1px solid ${CREAM}10`,
                  fontFamily: "'Archivo', sans-serif", fontSize: 11,
                  letterSpacing: "0.15em", textTransform: "uppercase",
                  opacity: 0.65,
                }}>
                  {r.label}
                </td>
                {COMPARISON.cols.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      padding: "20px", borderBottom: `1px solid ${CREAM}10`,
                      background: c.accent ? `${GOLD}0d` : "transparent",
                      fontFamily: "'Archivo', sans-serif", fontSize: 15,
                      color: c.accent ? CREAM : `${CREAM}b3`,
                      fontWeight: c.accent ? 500 : 400,
                    }}
                  >
                    {c.accent && <span style={{ color: GOLD, marginRight: 8, fontSize: 12 }}>✓</span>}
                    {r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);


// ============================================================================
// 6. STICKY CTA BAR — appears after first viewport scroll
// ============================================================================
export const StickyCTABar = ({
  title,
  subtitle,
  ctaText = "Ver precios",
  ctaHref = "#pricing",
  secondaryText,
  secondaryHref,
  testid = "sticky-cta",
}) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 600 && !dismissed) setVisible(true);
      else if (window.scrollY <= 400) setVisible(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  if (dismissed) return null;
  const isRoute = ctaHref.startsWith("/") && !ctaHref.includes("#");

  return (
    <div
      data-testid={testid}
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
        transform: visible ? "translateY(0)" : "translateY(120%)",
        transition: "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <div style={{
        maxWidth: 1200, margin: "0 auto", padding: "16px 24px",
        background: BG, border: `1px solid ${GOLD}44`, borderBottom: "none",
        boxShadow: `0 -20px 60px rgba(15,56,44,0.10), 0 -2px 0 ${GOLD}`,
        display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
      }}>
        <div style={{ flex: "1 1 260px", minWidth: 200 }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 4 }} data-testid={`${testid}-title`}>
            {title}
          </div>
          <div className="serif" style={{ fontSize: 15, color: CREAM, opacity: 0.85, fontStyle: "normal" }} data-testid={`${testid}-sub`}>
            {subtitle}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {secondaryText && secondaryHref && (
            <a
              href={secondaryHref}
              data-testid={`${testid}-secondary`}
              className="mono"
              style={{
                padding: "12px 16px", fontSize: 10, letterSpacing: "0.2em",
                textTransform: "uppercase", color: CREAM, opacity: 0.7,
                textDecoration: "none", border: `1px solid ${CREAM}22`,
              }}
            >
              {secondaryText}
            </a>
          )}
          <a
            href={ctaHref}
            data-testid={`${testid}-primary`}
            className="mono"
            style={{
              padding: "14px 22px", fontSize: 10.5, letterSpacing: "0.22em",
              textTransform: "uppercase", color: "#141414", background: LIME, borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414",
              fontWeight: 700, textDecoration: "none",
            }}
          >
            {ctaText} →
          </a>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid={`${testid}-close`}
            aria-label="Dismiss"
            style={{
              width: 32, height: 32, background: "transparent",
              border: `1px solid ${CREAM}22`, color: CREAM, opacity: 0.55,
              cursor: "pointer", fontSize: 14, marginLeft: 4,
            }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// 7. PROBLEM SECTION — 3 pain points
// ============================================================================
const PROBLEMS = [
  { n: "01", t1: "Los fundadores", t2: "se ", hl: "sobrediluyen.", d: "Regalan 20–40% de su empresa para levantar un capital que pudo ser deuda con 0–2% de dilución. Nadie te enseña la diferencia.", tint: "#EEF0FF", num: COBALT, glyph: "pie", art: COBALT, label: "20–40%", sub: "de tu empresa" },
  { n: "02", t1: "Los prestamistas", t2: "no tienen ", hl: "pipeline.", d: "Los fondos institucionales tienen miles de millones parados, pero no encuentran plataformas de crédito listas y ya analizadas en LATAM. La puerta fría no sirve.", tint: "#F3FAD6", num: "#8CA00E", glyph: "lock", art: LIME, label: "", sub: "Capital bloqueado" },
  { n: "03", t1: "Las PyMEs", t2: "pagan ", hl: "3x de más.", d: "Demasiadas PyMEs terminan en factoring al 30–40% anual porque su banquero nunca les mostró el warehouse que sí calificaban.", tint: "#F1EBFB", num: PURPLE_ACCENT, glyph: "file", art: PURPLE_ACCENT, label: "30–40%", sub: "factoring anual" },
];

export const ProblemSection = ({ testid = "problem" }) => (
  <section data-testid={testid} id="problem" style={{ padding: "90px 40px", position: "relative", zIndex: 5 }}>
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 18, fontWeight: 700 }}>— LOS TRES ERRORES DE SIEMPRE</div>
      <h2 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(34px, 4.6vw, 62px)", letterSpacing: "-0.035em", lineHeight: 0.98, margin: 0, maxWidth: 900 }}>
        La deuda institucional está rota. <span style={{ color: GOLD }}>A propósito.</span>
      </h2>
      <p style={{ marginTop: 20, fontFamily: "'Archivo', sans-serif", fontSize: 18, lineHeight: 1.5, opacity: 0.7, maxWidth: 720 }}>
        No porque los que operan sean malos. Sino porque nadie fuera de la industria te enseña cómo funciona de verdad — y a los prestamistas les conviene que siga siendo opaco.
      </p>

      <div style={{ marginTop: 44, display: "flex", flexDirection: "column", gap: 20 }}>
        {PROBLEMS.map((p, i) => (
          <div key={i} data-testid={`${testid}-card-${i}`} className="prob-row" style={{ background: p.tint, borderRadius: 24, display: "grid", gridTemplateColumns: "auto 1fr 260px", gap: 24, alignItems: "center", padding: "28px 34px", overflow: "hidden" }}>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(46px, 6vw, 74px)", color: p.num, lineHeight: 0.8, letterSpacing: "-0.04em", borderRight: `2px solid ${p.num}33`, paddingRight: 24, alignSelf: "start" }}>{p.n}</div>
            <div>
              <h3 style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(24px, 2.8vw, 34px)", letterSpacing: "-0.02em", lineHeight: 1.02, color: "#141414" }}>
                {p.t1}<br />{p.t2}<span style={{ color: GOLD }}>{p.hl}</span>
              </h3>
              <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.55, opacity: 0.75, maxWidth: 420, margin: "14px 0 0" }}>{p.d}</p>
            </div>
            <div className="prob-art" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <GlassArt accent={p.art} glyphColor={p.art === LIME ? COBALT : p.art} glyph={p.glyph} label={p.label} sublabel={p.sub} width={250} height={220} sparkColor={p.num} />
            </div>
          </div>
        ))}
      </div>

      {/* CTA bar */}
      <div data-testid={`${testid}-cta-bar`} style={{ marginTop: 30, background: LIME, borderRadius: 22, border: "2.5px solid #141414", boxShadow: "6px 6px 0 #141414", padding: "22px 26px", display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
        <span style={{ width: 44, height: 44, borderRadius: "50%", background: "#141414", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={LIME}><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>
        </span>
        <div style={{ flex: "1 1 280px", minWidth: 220 }}>
          <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: "#141414", opacity: 0.7, fontWeight: 700 }}>¿List@ para levantar deuda como insider?</div>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 18, color: "#141414", marginTop: 4, letterSpacing: "-0.01em" }}>El próximo grupo cierra pronto · inscripciones abiertas</div>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <PillCTA href="#pricing" variant="ink" testid={`${testid}-cta`}>Ver precios e inscribirme</PillCTA>
          <PillCTA href="#biblioteca" variant="ghost" testid={`${testid}-cta-secondary`}>Descarga un ebook gratis</PillCTA>
        </div>
      </div>
    </div>
    <style>{`
      @media (max-width: 860px) {
        [data-testid="${testid}"] .prob-row { grid-template-columns: auto 1fr !important; }
        [data-testid="${testid}"] .prob-art { display: none !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 8. MEET ERNESTO — portrait + story + pull quote
// ============================================================================
export const MeetErnesto = ({ testid = "meet-ernesto" }) => (
  <section data-testid={testid} id="meet" style={{ padding: "100px 40px", position: "relative", zIndex: 5 }}>
    <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 60, alignItems: "start" }} className="meet-grid">
      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 18, fontWeight: 700 }}>— CONOCE A ERNESTO</div>
        <h2 style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(32px, 4.4vw, 58px)", letterSpacing: "-0.035em", lineHeight: 0.98, color: "#141414" }}>
          Dieciséis años <span style={{ color: GOLD }}>cerrando</span> los deals que otros no supieron <Highlight>estructurar.</Highlight>
        </h2>
        <p style={{ marginTop: 26, fontFamily: "'Archivo', sans-serif", fontSize: 18, lineHeight: 1.5, opacity: 0.85, maxWidth: 520, fontWeight: 500 }}>
          Managing Partner en ERGO Capital. Fundador de Sivo. He estructurado +1.2B USD en term sheets en LATAM, EE.UU. y Europa. Asesor de Trafalgar, Kapital México, BAASA y Grupo Fagro.
        </p>

        <div style={{ marginTop: 30, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }} className="meet-stats">
          <div style={{ background: LIME, border: "2.5px solid #141414", borderRadius: 18, boxShadow: "5px 5px 0 #141414", padding: "20px 18px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#141414" strokeWidth="1.8"><rect x="4" y="3" width="14" height="18" rx="2"/><path d="M8 7h6M8 11h6M8 15h4"/></svg>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 32, letterSpacing: "-0.03em", color: "#141414", marginTop: 12, lineHeight: 1 }}>$1.2B+</div>
            <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "#141414", opacity: 0.7, marginTop: 6 }}>en term sheets firmados</div>
          </div>
          <div style={{ background: "#FFFFFF", border: "2.5px solid #141414", borderRadius: 18, boxShadow: "5px 5px 0 #141414", padding: "20px 18px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#141414" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 32, letterSpacing: "-0.03em", color: "#141414", marginTop: 12, lineHeight: 1 }}>16+</div>
            <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "#141414", opacity: 0.7, marginTop: 6 }}>años operando</div>
          </div>
          <div style={{ background: COBALT, border: "2.5px solid #141414", borderRadius: 18, boxShadow: "5px 5px 0 #141414", padding: "20px 18px", position: "relative" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="1.8"><path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z"/><path d="M9 3v15M15 6v15"/></svg>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 32, letterSpacing: "-0.03em", color: "#FFFFFF", marginTop: 12, lineHeight: 1 }}>9</div>
            <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "#FFFFFF", opacity: 0.85, marginTop: 6 }}>jurisdicciones</div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 14, bottom: 14 }}><path d="M4 12h15M13 6l6 6-6 6"/></svg>
          </div>
        </div>

        <div style={{ marginTop: 28, border: "2px solid #141414", borderRadius: 20, padding: "26px 28px", position: "relative", background: BG_ALT }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <span style={{ flexShrink: 0, marginTop: 2 }}><svg width="28" height="22" viewBox="0 0 30 24" fill={GOLD}><path d="M0 24V12C0 5 4 1 11 0l1 4C8 5 6 7 6 11h5v13zM17 24V12c0-7 4-11 11-12l1 4c-4 1-6 3-6 7h5v13z"/></svg></span>
            <p style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontSize: 16.5, lineHeight: 1.55, color: "#141414" }}>
              He estado de los dos lados: el fundador desesperado por su primera línea y el fondo que rechaza 200+ deals por trimestre. Todo lo que ves en esta página es el <Highlight>playbook de operador</Highlight> que hubiera querido tener cuando firmé mi primer mal term sheet en 2015.
            </p>
          </div>
          <Scribble arrow="none" size={17} style={{ position: "absolute", right: 22, bottom: -12, background: BG_ALT, padding: "0 8px" }}>De la experiencia a un framework real.</Scribble>
        </div>
      </div>

      <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
        <div style={{ position: "absolute", top: 22, right: 6, width: "82%", height: "82%", background: LIME, borderRadius: 22, transform: "rotate(4deg)", zIndex: 0 }} />
        <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 420, aspectRatio: "3 / 4", borderRadius: 22, border: "2.5px solid #141414", boxShadow: "8px 8px 0 #141414", overflow: "hidden", background: "url('https://customer-assets-7cd3h4nn.emergentagent.net/job_raise-like-insider/artifacts/49gzz0ou_31066C61-A699-4CE6-96B2-1FEA5AB108AD.png') center 15% / cover" }}>
          <div style={{ position: "absolute", left: 16, bottom: 16, transform: "rotate(-4deg)" }}>
            <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#FFFFFF", background: COBALT, borderRadius: 8, padding: "6px 10px", display: "inline-block" }}>Managing Partner · ERGO Capital</span>
          </div>
        </div>
        <Scribble arrow="down-right" size={20} style={{ position: "absolute", top: -18, left: -4, zIndex: 3 }}>Ernesto<br />Gómez Berjón</Scribble>
      </div>
    </div>
    <style>{`
      @media (max-width: 900px) {
        [data-testid="${testid}"] .meet-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
      }
      @media (max-width: 520px) {
        [data-testid="${testid}"] .meet-stats { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 8b. FORBES / PRESS STRIP — reusable video proof section
// ============================================================================
const FORBES_VIDEOS = [
  { id: "L4ILB77pxUo", title: "Entrevista a Ernesto Gómez · Finmex", channel: "FranchiseZar · Entrevista", thumb: "/forbes-ernesto-finmex.jpg" },
  { id: "7SAmE2wMLb4", title: "La Evolución de la banca en México", channel: "Foro Forbes · 2015" },
  { id: "TqlbfZ1m7gQ", title: "Finmex: 8 años con los emprendedores", channel: "Foro Forbes Mujeres Poderosas" },
].map((v) => ({ ...v, url: `https://www.youtube.com/watch?v=${v.id}`, thumb: v.thumb || `https://img.youtube.com/vi/${v.id}/hqdefault.jpg` }));

export const ForbesStrip = ({ testid = "forbes" }) => (
  <section style={{ padding: "80px 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid={testid}>
    <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
      — PRENSA & FORO FORBES
    </div>
    <h2 className="serif" style={{ fontSize: "clamp(30px, 3.6vw, 52px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.05, margin: 0, maxWidth: 900 }}>
      Ernesto en los <span style={{ fontStyle: "normal", color: GOLD }}>foros de Forbes.</span>
    </h2>
    <div style={{ marginTop: 44, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
      {FORBES_VIDEOS.map((v, i) => (
        <a key={i} href={v.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-video-${i}`}
          style={{ textDecoration: "none", color: "inherit", display: "block" }}>
          <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "2px solid #141414", boxShadow: "5px 5px 0 #141414", aspectRatio: "16 / 9", background: "#000" }}>
            <img src={v.thumb} alt={v.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.92 }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: LIME, border: "2px solid #141414", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#141414"><path d="M8 5v14l11-7z" /></svg>
              </div>
            </div>
          </div>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.24em", color: GOLD, textTransform: "uppercase", marginTop: 16 }}>{v.channel}</div>
          <div style={{ marginTop: 8, fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>{v.title}</div>
        </a>
      ))}
    </div>
  </section>
);


// ============================================================================
// 9. METHOD SECTION — 3-phase framework
// ============================================================================
const METHOD_PHASES = [
  {
    n: "01",
    t: "Diagnosticar",
    d: "Revisamos tu estructura de capital, tu costo de capital y tu cap table. Sale un memo de 12 páginas, listo para compartir, que puntúa tu readiness en 6 dimensiones.",
    steps: ["Revisión de cap table y waterfall", "Calculadora de costo de capital", "Scorecard de readiness"],
    weeks: "semana 1-2",
  },
  {
    n: "02",
    t: "Estructurar",
    d: "Te llevas 3 plantillas de term sheet pre-negociadas, una taxonomía de covenants y acceso a nuestro formato de loan tape. Lo que usan los mejores equipos — sin adivinar.",
    steps: ["Plantillas de term sheet", "Taxonomía de covenants", "Formato loan tape v3.2"],
    weeks: "semana 3-6",
  },
  {
    n: "03",
    t: "Cerrar",
    d: "Warm intros a +60 prestamistas curados según tu tesis. Office hours en vivo conmigo cada semana. Armamos tu data room. Primer cierre en 8-10 semanas — no en meses.",
    steps: ["Warm intros a +60 fondos", "Armado del data room", "Office hours en vivo cada semana"],
    weeks: "semana 7-10",
  },
];

export const MethodSection = ({ testid = "method" }) => (
  <section
    data-testid={testid}
    id="method"
    style={{ padding: "120px 40px", position: "relative", zIndex: 5, borderTop: "1px solid rgba(15,56,44,0.06)" }}
  >
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
        — EL MÉTODO
      </div>
      <h2 className="serif" style={{ fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300, letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, maxWidth: 1000, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
        Tres fases. <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>Diez semanas.</span> Un primer cierre.
      </h2>
      <div style={{ marginTop: 60, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }} className="method-grid">
        {METHOD_PHASES.map((p, i) => (
          <div key={i} data-testid={`${testid}-phase-${i}`} style={{ background: BG_ALT, border: "2px solid #141414", borderRadius: 16, boxShadow: "4px 4px 0 #141414", padding: 36, position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div className="serif" style={{ fontSize: 56, fontWeight: 300, color: GOLD, letterSpacing: "-0.03em", lineHeight: 1, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>{p.n}</div>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.22em", textTransform: "uppercase", color: CREAM, opacity: 0.55, border: `1px solid ${GOLD}44`, padding: "4px 8px" }}>{p.weeks}</div>
            </div>
            <div className="serif" style={{ fontSize: 30, fontWeight: 400, marginTop: 20, letterSpacing: "-0.02em", lineHeight: 1, color: CREAM, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>{p.t}.</div>
            <p style={{ marginTop: 16, fontSize: 15, lineHeight: 1.6, opacity: 0.78, margin: "16px 0 0" }}>{p.d}</p>
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(15,56,44,0.08)", display: "flex", flexDirection: "column", gap: 10 }}>
              {p.steps.map((s, j) => (
                <div key={j} className="mono" style={{ fontSize: 10.5, letterSpacing: "0.12em", color: CREAM, opacity: 0.75, display: "flex", gap: 12, alignItems: "baseline" }}>
                  <span style={{ display: "inline-block", width: 12, height: 1, background: GOLD, flexShrink: 0, alignSelf: "center" }} />
                  {s}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <style>{`
      @media (max-width: 900px) { .method-grid { grid-template-columns: 1fr !important; } }
    `}</style>
  </section>
);


// ============================================================================
// 10. VALUE LADDER — 4 grouped tiers (Gratis · Autoservicio · Suscripción · Premium)
// ============================================================================
const CTA_VARIANTS = {
  lime: { background: LIME, color: "#141414" },
  cobalt: { background: BLUE, color: "#FFFFFF" },
  ink: { background: "#141414", color: "#FFFFFF" },
  white: { background: "#FFFFFF", color: "#141414" },
};
const ctaStyle = (variant = "lime", big = false) => ({
  display: "block", width: "100%", textAlign: "center", textDecoration: "none",
  padding: big ? "17px 20px" : "12px 12px",
  fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: big ? 13.5 : 11,
  letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.2,
  border: "2px solid #141414", borderRadius: 12, boxShadow: "4px 4px 0 #141414",
  cursor: "pointer", ...(CTA_VARIANTS[variant] || CTA_VARIANTS.lime),
});
const BuyCTA = ({ lookupKey, children, testid, variant = "lime", big = false }) => (
  <button type="button" className="maca-cta" data-testid={testid} onClick={() => launchCheckout(lookupKey)} style={ctaStyle(variant, big)}>
    {children}
  </button>
);
const LinkCTA = ({ href, children, testid, variant = "lime", big = false }) => (
  <a className="maca-cta" data-testid={testid} href={href} style={ctaStyle(variant, big)}>
    {children}
  </a>
);
const ApplyCTA = ({ applyKey, formHref, children, testid, variant = "lime", big = false }) => (
  <button type="button" className="maca-cta" data-testid={testid} onClick={() => launchCheckout(applyKey, { formHref })} style={ctaStyle(variant, big)}>
    {children}
  </button>
);
const TierBadge = ({ text, bg = LIME, color = "#141414", rotate = -3 }) => (
  <span className="mono" style={{
    background: bg, color, border: "2px solid #141414", borderRadius: 10, boxShadow: "3px 3px 0 #141414",
    padding: "6px 12px", fontSize: 9.5, letterSpacing: "0.14em", fontWeight: 800, textTransform: "uppercase",
    transform: `rotate(${rotate}deg)`, display: "inline-block", whiteSpace: "nowrap",
  }}>{text}</span>
);
const Feat = ({ children, color = BLUE }) => (
  <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.4, opacity: 0.92 }}>
    <span style={{ color, fontWeight: 900, flexShrink: 0 }}>✓</span>
    <span>{children}</span>
  </div>
);
const TierHeader = ({ num, title, sub, badge }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 28, flexWrap: "wrap" }}>
    <div className="serif" style={{ fontSize: 52, fontWeight: 300, color: BLUE, letterSpacing: "-0.03em", lineHeight: 0.85, flexShrink: 0, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>{num}</div>
    <div style={{ flex: "1 1 260px", minWidth: 220 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 3vw, 34px)", letterSpacing: "-0.02em", textTransform: "uppercase", color: "#141414" }}>{title}</h3>
        {badge}
      </div>
      <p style={{ marginTop: 8, fontSize: 15, lineHeight: 1.5, opacity: 0.72, maxWidth: 680 }}>{sub}</p>
    </div>
  </div>
);
const ladderCard = (bg = "#FFFFFF", fg = "#141414", lift = false) => ({
  background: bg, color: fg, border: "2px solid #141414", borderRadius: 18, boxShadow: "6px 6px 0 #141414",
  padding: 26, display: "flex", flexDirection: "column", position: "relative",
  transform: lift ? "translateY(-6px)" : "none",
});

// ============================================================================
// 10b. VERTICAL OFFERS — vertical-first ladder (ebook · curso · The Debt Program)
//      + materiales incluidos + urgencia. Replaces the 4-tier ValueLadder.
// ============================================================================
const priceLabel = (p) => (p.interval ? `${p.usd}/${p.interval}` : p.usd);

const OFFER_VERTICALS = [
  {
    id: "startup", label: "Startups", audience: "Pre-seed · Seed · Serie A", seats: 4, cycleDays: 10, illustration: "/ill-rocket.png",
    tagline: "Arma tu primera ronda como insider — equity + tu primera línea de deuda, sin regalar el 30% de tu empresa.",
    ebook: "ebook_startups", course: "course_startups", cohort: "cohort_startups",
    materials: [
      { key: "toolkit", note: "Modelos de cap table, SAFE y checklist de data room." },
      { key: "circle_founder", note: "Comunidad privada + office hours en vivo conmigo." },
    ],
  },
  {
    id: "pyme", label: "PyMEs", audience: "Negocios con facturación real", seats: 6, cycleDays: 14, illustration: "/ill-store.png",
    tagline: "Capital de trabajo o crédito bancario sin regalar tu empresa ni caer en factoring al 40%.",
    ebook: "ebook_pymes", course: "course_pymes", cohort: "cohort_pymes",
    materials: [
      { key: "toolkit", note: "Modelo de PyME en Excel + checklist de due diligence bancario." },
      { key: "course_intangibles", note: "Valúa marca, IP y cartera recurrente como colateral." },
      { key: "circle_founder", note: "Acompañamiento continuo mes con mes." },
    ],
  },
  {
    id: "lender", label: "Lenders · Instituciones", audience: "Fintechs · SOFOMs · fondos · family offices", seats: 3, cycleDays: 7, illustration: "/ill-bank.png",
    tagline: "Estructura tu warehouse y cierra tu primera línea institucional de $5M a $50M+.",
    ebook: "ebook_lenders", course: "course_lenders", cohort: "cohort_lenders",
    materials: [
      { key: "toolkit", note: "Modelo de cartera, taxonomía de covenants y loan tape." },
      { key: "course_intangibles", note: "Valuación de cartera recurrente / ARR como colateral." },
      { key: "cert_fincorp", note: "FinCorp + Certificación avalada por IADA Anáhuac." },
      { key: "circle_founder", note: "Red de deal-flow institucional + directorio de fondos." },
    ],
  },
];

const COHORT_FEATS = [
  "Acompañamiento en vivo con Ernesto",
  "Data room, modelo y teaser armados contigo",
  "Revisión 1:1 de tu term sheet · cupo limitado",
];

const COHORT_CLOSE_ANCHOR = Date.UTC(2026, 0, 1);
const nextDeadline = () => {
  // Single source of truth: the next 28th of the month (matches Hub countdown).
  const now = new Date();
  let t = new Date(now.getFullYear(), now.getMonth(), 28, 23, 59, 59);
  if (t.getTime() <= now.getTime()) t = new Date(now.getFullYear(), now.getMonth() + 1, 28, 23, 59, 59);
  return t.getTime();
};
const MONTHS_ES_CK = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const closeLabelCK = () => {
  const d = new Date(nextDeadline());
  return `Cierra el ${d.getDate()} de ${MONTHS_ES_CK[d.getMonth()]}, ${d.getFullYear()}`;
};
const useCountdown = (target) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 86400), 1000);
    return () => clearInterval(id);
  }, []);
  let diff = Math.max(0, target - Date.now());
  const d = Math.floor(diff / 86400000); diff -= d * 86400000;
  const h = Math.floor(diff / 3600000); diff -= h * 3600000;
  const m = Math.floor(diff / 60000); diff -= m * 60000;
  const s = Math.floor(diff / 1000);
  return { d, h, m, s };
};

const OfferRung = ({ eyebrow, title, product, blurb, feats, ctaLabel, ctaTestid, popular, flagship }) => {
  const cardStyle = {
    background: flagship ? CREAM : BG_ALT,
    color: flagship ? "#FFFFFF" : CREAM,
    border: `2px solid ${flagship ? BLUE : "#141414"}`,
    borderRadius: 18,
    boxShadow: flagship ? `7px 7px 0 ${BLUE}` : "6px 6px 0 #141414",
    padding: 26, display: "flex", flexDirection: "column", position: "relative",
    ...(flagship ? { transform: "translateY(-6px)" } : {}),
  };
  return (
    <div className="vo-card" style={cardStyle}>
      {(popular || flagship) && (
        <div style={{ position: "absolute", top: -14, left: 20 }}>
          <TierBadge text={flagship ? "Flagship · cupos limitados" : "Más vendido"} bg={LIME} rotate={flagship ? -3 : -4} />
        </div>
      )}
      <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: flagship ? LIME : BLUE, fontWeight: 700, marginTop: 6 }}>{eyebrow}</div>
      <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1, color: flagship ? "#FFFFFF" : CREAM }}>{title}</div>
      <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div className="serif" style={{ fontSize: 40, color: flagship ? LIME : BLUE, letterSpacing: "-0.03em", lineHeight: 1 }}>{priceLabel(product)} <span style={{ fontSize: 13, opacity: 0.6, fontWeight: 700 }}>USD</span></div>
        <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.08em", opacity: 0.6 }}>≈ {product.mxn}</div>
      </div>
      <p style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.5, opacity: flagship ? 0.9 : 0.8, flex: feats ? 0 : 1 }}>{blurb}</p>
      {feats && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          {feats.map((f, i) => <Feat key={i} color={flagship ? LIME : BLUE}>{f}</Feat>)}
        </div>
      )}
      <div style={{ marginTop: 18 }}>
        <BuyCTA lookupKey={product.key} testid={ctaTestid} variant={flagship ? "lime" : (popular ? "lime" : "cobalt")} big>{ctaLabel}</BuyCTA>
        {product.installmentKey && (
          <button
            type="button"
            data-testid={`installment-${product.key}`}
            onClick={() => launchCheckout(product.installmentKey)}
            className="mono"
            style={{ display: "block", marginTop: 10, background: "transparent", border: "none", padding: 0, cursor: "pointer", color: flagship ? LIME : BLUE, fontSize: 11.5, letterSpacing: "0.04em", textDecoration: "underline", textUnderlineOffset: 3 }}
          >
            o 3 pagos de {product.installmentUsd}/mes →
          </button>
        )}
        {flagship && <PayPalButton lookupKey={product.key} testid={`paypal-${product.key}`} />}
      </div>
    </div>
  );
};

export const VerticalOffers = ({ testid = "pricing" }) => {
  const [active, setActive] = useState("pyme");
  const v = OFFER_VERTICALS.find((x) => x.id === active) || OFFER_VERTICALS[0];
  const eb = PRODUCTS[v.ebook], co = PRODUCTS[v.course], ch = PRODUCTS[v.cohort];
  const cd = useCountdown(nextDeadline());

  // Allow other sections (e.g. Hub track cards) to preselect a vertical tab.
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      if (id && OFFER_VERTICALS.some((x) => x.id === id)) setActive(id);
    };
    window.addEventListener("tcg:select-vertical", handler);
    const m = typeof window !== "undefined" && window.location.hash.match(/#pricing-(\w+)/);
    if (m && OFFER_VERTICALS.some((x) => x.id === m[1])) setActive(m[1]);
    return () => window.removeEventListener("tcg:select-vertical", handler);
  }, []);

  return (
    <section
      data-testid={testid}
      id="pricing"
      style={{ padding: "110px 40px", background: BG_ALT, borderTop: "1px solid rgba(15,56,44,0.06)", position: "relative", zIndex: 5 }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 20, fontWeight: 700 }}>
          — ELIGE TU VERTICAL · COMPRA EN UN CLIC
        </div>
        <h2 className="serif" style={{ fontSize: "clamp(34px, 4.5vw, 64px)", letterSpacing: "-0.035em", lineHeight: 1.0, margin: 0, maxWidth: 1000, fontWeight: 900 }}>
          Ebook, curso y cohorte. <span style={{ color: GOLD }}>Para tu caso exacto.</span>
        </h2>
        <p style={{ marginTop: 18, fontSize: 18, lineHeight: 1.55, opacity: 0.75, maxWidth: 720 }}>
          Mismo método, tres rutas: <strong>léelo</strong>, <strong>ejecútalo tú</strong> o <strong>entra al programa y lo cerramos juntos</strong>. Elige tu vertical y empieza hoy.
        </p>

        {/* FREE TIER · Ebook gratis */}
        <div className="vo-free" style={{ marginTop: 36, background: LIME, border: "2px solid #141414", borderRadius: 16, boxShadow: "5px 5px 0 #141414", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 800 }}>Gratis · empieza hoy</div>
            <div className="serif" style={{ fontSize: 24, marginTop: 6, letterSpacing: "-0.02em", lineHeight: 1.05 }}>Descarga tu ebook gratis — el método, en PDF.</div>
          </div>
          <a className="maca-cta" data-testid="cta-free-ebook" href="#biblioteca" style={{ ...ctaStyle("ink", true), width: "auto", padding: "16px 26px", whiteSpace: "nowrap" }}>Descargar mi ebook gratis →</a>
        </div>

        {/* VERTICAL TABS */}
        <div className="vo-tabs" role="tablist" style={{ marginTop: 40, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {OFFER_VERTICALS.map((t) => {
            const on = t.id === active;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={on}
                data-testid={`tab-vertical-${t.id}`}
                onClick={() => setActive(t.id)}
                className="maca-cta"
                style={{
                  flex: "1 1 200px", minWidth: 180, textAlign: "left", cursor: "pointer",
                  background: on ? BLUE : BG_ALT, color: on ? "#FFFFFF" : CREAM,
                  border: "2px solid #141414", borderRadius: 14,
                  boxShadow: on ? "5px 5px 0 #141414" : "3px 3px 0 #141414",
                  padding: "16px 18px", transform: on ? "translateY(-2px)" : "none",
                }}
              >
                <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 16, letterSpacing: "-0.01em", textTransform: "uppercase" }}>Para {t.label}</div>
                <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", opacity: on ? 0.85 : 0.55, marginTop: 6 }}>{t.audience}</div>
              </button>
            );
          })}
        </div>

        <div className="vo-active-head" style={{ marginTop: 26, display: "grid", gridTemplateColumns: "1fr 150px", gap: 24, alignItems: "center" }} data-testid={`vo-head-${v.id}`}>
          <div style={{ fontSize: 15.5, lineHeight: 1.5, opacity: 0.82, maxWidth: 700, fontWeight: 500 }} data-testid={`vo-tagline-${v.id}`}>{v.tagline}</div>
          <img src={v.illustration} alt="" loading="lazy" className="vo-art" style={{ width: "100%", maxWidth: 150, height: "auto", justifySelf: "end" }} />
        </div>

        {/* 3-RUNG LADDER */}
        <div className="vo-grid" style={{ marginTop: 30, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18, alignItems: "stretch" }}>
          <OfferRung eyebrow="01 · Ebook" title="Ebook" product={eb} blurb={eb.blurb} ctaLabel={`Comprar ebook · ${eb.usd} →`} ctaTestid={`buy-${v.ebook}`} />
          <OfferRung eyebrow="02 · Curso aplicado" title="Curso paso a paso" product={co} blurb={co.blurb} ctaLabel={`Comprar curso · ${co.usd} →`} ctaTestid={`buy-${v.course}`} popular />
          <OfferRung eyebrow="03 · Cohorte" title="The Debt Program" product={ch} blurb={ch.blurb} feats={COHORT_FEATS} ctaLabel="Inscribirme ahora →" ctaTestid={`enroll-${v.cohort}`} flagship />
        </div>

        {/* 1:1 · Capital Markets as a Service (compra directa + 3 pagos) */}
        <div data-testid="offer-1to1" style={{ marginTop: 40, background: CREAM, color: "#FFFFFF", border: `2px solid ${BLUE}`, borderRadius: 18, boxShadow: `7px 7px 0 ${BLUE}`, padding: "30px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 440px", minWidth: 280 }}>
            <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: LIME, fontWeight: 800 }}>¿Prefieres que lo haga contigo? · 1:1 a la medida</div>
            <div className="serif" style={{ fontSize: 30, marginTop: 10, letterSpacing: "-0.025em", lineHeight: 1.02 }}>Capital Markets as a Service</div>
            <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, opacity: 0.9, maxWidth: 580 }}>{PRODUCTS.consulting_1to1.blurb}</p>
          </div>
          <div style={{ textAlign: "right", flex: "0 0 auto", minWidth: 220 }}>
            <div className="serif" style={{ fontSize: 40, color: LIME, letterSpacing: "-0.03em", lineHeight: 1 }}>{PRODUCTS.consulting_1to1.usd} <span style={{ fontSize: 13, opacity: 0.7, fontWeight: 700 }}>USD</span></div>
            <div className="mono" style={{ fontSize: 10.5, opacity: 0.7, marginTop: 5 }}>10 horas · ≈ {PRODUCTS.consulting_1to1.mxn}</div>
            <div style={{ marginTop: 16 }}>
              <BuyCTA lookupKey="consulting_1to1" testid="buy-consulting_1to1" variant="lime" big>Reservar mis 10 horas →</BuyCTA>
            </div>
            <button type="button" data-testid="installment-consulting_1to1" onClick={() => launchCheckout("consulting_1to1_3x")} className="mono" style={{ display: "block", marginLeft: "auto", marginTop: 10, background: "transparent", border: "none", padding: 0, cursor: "pointer", color: LIME, fontSize: 11.5, letterSpacing: "0.04em", textDecoration: "underline", textUnderlineOffset: 3 }}>o 3 pagos de {PRODUCTS.consulting_1to1.installmentUsd}/mes →</button>
            <PayPalButton lookupKey="consulting_1to1" testid="paypal-consulting_1to1" />
          </div>
        </div>

        {/* INCLUDED MATERIALS */}
        <div style={{ marginTop: 44 }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", color: BLUE, textTransform: "uppercase", fontWeight: 700, marginBottom: 16 }}>
            — Materiales que puedes sumar a tu vertical
          </div>
          <div className="vo-mat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            {v.materials.map((m) => {
              const p = PRODUCTS[m.key];
              return (
                <div key={m.key} style={{ background: BG, border: "2px solid #141414", borderRadius: 14, boxShadow: "4px 4px 0 #141414", padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                    <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>{p.name}</div>
                    <div className="serif" style={{ fontSize: 18, color: BLUE, whiteSpace: "nowrap" }}>{priceLabel(p)}</div>
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.45, opacity: 0.75, flex: 1 }}>{m.note}</div>
                  <BuyCTA lookupKey={p.key} testid={`add-${m.key}-${v.id}`} variant="white">Comprar →</BuyCTA>
                </div>
              );
            })}
          </div>
        </div>

        {/* URGENCY STRIP + COUNTDOWN */}
        <div style={{ marginTop: 36, background: CREAM, color: "#FFFFFF", border: "2px solid #141414", borderRadius: 16, boxShadow: `5px 5px 0 ${BLUE}`, padding: "22px 24px", display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }} data-testid="vo-urgency">
          <span style={{ background: LIME, color: "#141414", borderRadius: 8, padding: "5px 11px", fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Últimos {v.seats} cupos</span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.7, marginBottom: 8 }}>Cierra la inscripción de {v.label} en</div>
            <div data-testid="vo-countdown" style={{ display: "flex", gap: 8 }}>
              {[["DÍAS", cd.d], ["HRS", cd.h], ["MIN", cd.m], ["SEG", cd.s]].map(([lbl, val]) => (
                <div key={lbl} style={{ background: "#FFFFFF", color: "#141414", borderRadius: 8, padding: "7px 10px", minWidth: 46, textAlign: "center" }}>
                  <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: "-0.02em" }}>{String(val).padStart(2, "0")}</div>
                  <div className="mono" style={{ fontSize: 8, letterSpacing: "0.1em", opacity: 0.55, marginTop: 2 }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
          <span style={{ fontSize: 13, opacity: 0.85, flex: "1 1 240px", minWidth: 220 }}>Precio garantizado esta semana · revisión 1:1 de tu data room conmigo · acceso inmediato al material.</span>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          [data-testid="${testid}"] .vo-grid { grid-template-columns: 1fr !important; }
          [data-testid="${testid}"] .vo-card { transform: none !important; }
          [data-testid="${testid}"] .vo-active-head { grid-template-columns: 1fr !important; }
          [data-testid="${testid}"] .vo-art { display: none !important; }
        }
      `}</style>
    </section>
  );
};

export const ValueLadder = ({ testid = "pricing" }) => (
  <section
    data-testid={testid}
    id="pricing"
    style={{ padding: "120px 40px", background: BG_ALT, position: "relative", zIndex: 5, borderTop: "1px solid rgba(15,56,44,0.06)" }}
  >
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22, fontWeight: 700 }}>
        — CÓMO TRABAJAMOS JUNTOS
      </div>
      <h2 className="serif" style={{ fontSize: "clamp(34px, 4.5vw, 66px)", letterSpacing: "-0.035em", lineHeight: 1.0, margin: 0, maxWidth: 1000 }}>
        De gratis a 1:1. <span style={{ color: GOLD }}>Un solo objetivo.</span>
      </h2>
      <p style={{ marginTop: 20, fontSize: 18, lineHeight: 1.55, opacity: 0.75, maxWidth: 760 }}>
        Cuatro niveles para acompañarte desde el primer term sheet hasta levantar deuda institucional conmigo. Empieza donde estés hoy.
      </p>

      <div style={{ marginTop: 64, display: "flex", flexDirection: "column", gap: 68 }}>

        {/* ===== TIER 1 · GRATIS ===== */}
        <div data-testid={`${testid}-tier-gratis`}>
          <TierHeader num="01" title="Gratis" sub="Conocimiento accionable, sin costo, directo a tu correo." badge={<TierBadge text="Entrada libre" bg={LIME} rotate={-3} />} />
          <div style={ladderCard(LIME, "#141414")}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "start" }} className="ladder-free-grid">
              <div>
                <div className="serif" style={{ fontSize: 32, letterSpacing: "-0.02em", lineHeight: 1 }}>El Teardown</div>
                <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.5, maxWidth: 540 }}>
                  Desarmo operaciones reales de capital, por dentro. Sin choro, pura mecánica.
                </p>
                <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8, maxWidth: 480 }}>
                  <Feat>Deals reales de capital, desmenuzados</Feat>
                  <Feat>Fundadores, CFOs y analistas adentro</Feat>
                  <Feat>El Módulo 1 del curso, gratis al entrar</Feat>
                </div>
              </div>
              <div style={{ textAlign: "right", minWidth: 140 }}>
                <div className="serif" style={{ fontSize: 60, letterSpacing: "-0.03em", lineHeight: 1 }}>$0</div>
                <div className="mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.7, marginTop: 4 }}>para siempre</div>
              </div>
            </div>
            <div style={{ marginTop: 24, maxWidth: 360 }}>
              <LinkCTA href="#biblioteca" testid="cta-gratis-ebook" variant="ink" big>Descargar gratis →</LinkCTA>
            </div>
          </div>
        </div>

        {/* ===== TIER 2 · AUTOSERVICIO ===== */}
        <div data-testid={`${testid}-tier-autoservicio`}>
          <TierHeader num="02" title="Autoservicio" sub="Compras y accedes ya. Sin aplicar, sin esperar." badge={<TierBadge text="Acceso inmediato" bg="#FAF7EF" rotate={2} />} />
          <div style={{ background: "#FAF7EF", border: "2px solid #141414", borderRadius: 20, boxShadow: "6px 6px 0 #141414", padding: 26 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }} className="ladder-grid">

            {/* Ebooks */}
            <div style={ladderCard()}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700 }}>Ebooks por vertical</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>Deuda, por dentro</div>
              <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8 }}>
                <div className="serif" style={{ fontSize: 40, color: BLUE, letterSpacing: "-0.03em", lineHeight: 1 }}>$19</div>
                <div className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7 }}>c/u · bundle $39</div>
              </div>
              <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.45, opacity: 0.8, flex: 1 }}>PyMEs, Lenders / Instituciones o FinCorp. Compra directa, lectura inmediata.</p>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 7 }}>
                <BuyCTA lookupKey="ebook_pymes" testid="cta-ebook-pymes" variant="white">PyMEs · $19</BuyCTA>
                <BuyCTA lookupKey="ebook_lenders" testid="cta-ebook-lenders" variant="white">Lenders · $19</BuyCTA>
                <BuyCTA lookupKey="ebook_fincorp" testid="cta-ebook-fincorp" variant="white">FinCorp · $19</BuyCTA>
              </div>
              <div style={{ marginTop: 8 }}>
                <BuyCTA lookupKey="ebook_bundle" testid="cta-ebook-bundle" variant="cobalt">Bundle 3 · $39 →</BuyCTA>
              </div>
            </div>

            {/* Toolkit */}
            <div style={ladderCard()}>
              <div style={{ position: "absolute", top: -14, left: 20 }}><TierBadge text="Más popular" bg={LIME} rotate={-4} /></div>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700, marginTop: 6 }}>Herramientas</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>The Debt Toolkit</div>
              <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8 }}>
                <div className="serif" style={{ fontSize: 40, color: BLUE, letterSpacing: "-0.03em", lineHeight: 1 }}>$149</div>
                <div className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7 }}>pago único</div>
              </div>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Feat>Modelos de cartera y de PyME en Excel</Feat>
                <Feat>Checklist de data room · 80 puntos</Feat>
                <Feat>Plantilla de term sheet anotada</Feat>
              </div>
              <div style={{ marginTop: 18 }}>
                <BuyCTA lookupKey="toolkit" testid="cta-toolkit" variant="lime" big>Comprar Toolkit · $149 →</BuyCTA>
              </div>
            </div>

            {/* Cursos */}
            <div style={ladderCard()}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700 }}>Cursos por vertical</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>Aprende aplicado</div>
              <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.45, opacity: 0.8, flex: 1 }}>Video-lecciones + plantillas reales. Elige tu vertical y compra directo.</p>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 7 }}>
                <BuyCTA lookupKey="course_pymes" testid="cta-course-pymes" variant="white">Curso PyMEs · $149</BuyCTA>
                <BuyCTA lookupKey="course_lenders" testid="cta-course-lenders" variant="white">Curso Lenders · $199</BuyCTA>
                <BuyCTA lookupKey="course_fincorp" testid="cta-course-fincorp" variant="ink">Curso FinCorp · $249</BuyCTA>
              </div>
            </div>

            {/* Certificación */}
            <div style={ladderCard(BLUE, "#FFFFFF")}>
              <div style={{ position: "absolute", top: -14, left: 20 }}><TierBadge text="Avalada Anáhuac" bg={LIME} rotate={3} /></div>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: LIME, fontWeight: 700, marginTop: 6 }}>Certificación</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>Certificación FinCorp</div>
              <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8 }}>
                <div className="serif" style={{ fontSize: 40, color: LIME, letterSpacing: "-0.03em", lineHeight: 1 }}>$2,500</div>
              </div>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Feat color={LIME}>6 módulos + proyecto final</Feat>
                <Feat color={LIME}>M&amp;A, bonos, emisiones e IPO</Feat>
                <Feat color={LIME}>Credencial verificable · aval IADA Anáhuac</Feat>
              </div>
              <div style={{ marginTop: 18 }}>
                <BuyCTA lookupKey="cert_fincorp" testid="cta-certificacion" variant="lime" big>Inscribirme · $2,500 →</BuyCTA>
              </div>
            </div>

            {/* Intangibles */}
            <div style={ladderCard()}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700 }}>Valuación de intangibles</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>Fondea lo intangible</div>
              <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.45, opacity: 0.8, flex: 1 }}>Valúa marca, IP, datos y cartera recurrente para levantar deuda contra ellos. Para Lenders y PyMEs.</p>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 7 }}>
                <BuyCTA lookupKey="ebook_intangibles" testid="cta-ebook-intangibles" variant="white">Ebook · $29</BuyCTA>
                <BuyCTA lookupKey="course_intangibles" testid="cta-course-intangibles" variant="ink">Curso · $299</BuyCTA>
              </div>
              <div style={{ marginTop: 8 }}>
                <LinkCTA href="/intangibles" testid="cta-intangibles-page" variant="white">Ver el vertical →</LinkCTA>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ===== TIER 3 · SUSCRIPCIÓN (hero) ===== */}
        <div data-testid={`${testid}-tier-suscripcion`}>
          <TierHeader num="03" title="Suscripción" sub="Acceso continuo: plantillas vivas, office hours, comunidad y deal-flow." badge={<TierBadge text="Membresía" bg="#141414" color="#FFFFFF" rotate={-2} />} />
          <div style={ladderCard("#141414", "#FFFFFF")}>
            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 40, alignItems: "center" }} className="ladder-circle-grid">
              <div>
                <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: LIME, fontWeight: 700 }}>Membresía · recurrente</div>
                <div className="serif" style={{ fontSize: 36, marginTop: 12, letterSpacing: "-0.02em", lineHeight: 1 }}>The Debt Circle</div>
                <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.5, opacity: 0.85, maxWidth: 520 }}>
                  Tu cuarto privado de capital: lo que necesitas para seguir estructurando mes con mes.
                </p>
                <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxWidth: 560 }}>
                  <Feat color={LIME}>Plantillas vivas, actualizadas cada mes</Feat>
                  <Feat color={LIME}>Office hours en vivo conmigo</Feat>
                  <Feat color={LIME}>Comunidad privada + deal-flow</Feat>
                  <Feat color={LIME}>Directorio de fondos verificado</Feat>
                </div>
              </div>
              <div style={{ background: "#FFFFFF", color: "#141414", border: "2px solid #141414", borderRadius: 16, boxShadow: "5px 5px 0 " + LIME, padding: 24 }}>
                <div style={{ display: "inline-block", marginBottom: 12 }}><TierBadge text="Fundador · primeros 100" bg={LIME} rotate={-3} /></div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div className="serif" style={{ fontSize: 54, color: BLUE, letterSpacing: "-0.03em", lineHeight: 1 }}>$49</div>
                  <div className="mono" style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.7 }}>/mes</div>
                </div>
                <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.08em", opacity: 0.6, marginTop: 4 }}>después $79/mes · o $790/año</div>
                <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                  <BuyCTA lookupKey="circle_founder" testid="cta-circle-founder" variant="lime" big>Unirme · $49/mes →</BuyCTA>
                  <BuyCTA lookupKey="circle_annual" testid="cta-circle-annual" variant="white">Plan anual · $790</BuyCTA>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== TIER 4 · PREMIUM ===== */}
        <div data-testid={`${testid}-tier-premium`}>
          <TierHeader num="04" title="Premium" sub="Cohortes acompañadas con inscripción directa, y el mandato 1:1 a la medida." badge={<TierBadge text="Cupo limitado" bg={BLUE} color="#FFFFFF" rotate={2} />} />
          <div style={{ background: BLUE, border: "2px solid #141414", borderRadius: 20, boxShadow: "6px 6px 0 #141414", padding: 26 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }} className="ladder-grid">

            {/* The Debt Program */}
            <div style={ladderCard("#FFFFFF", "#141414")}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700 }}>Cohorte · grupo acompañado</div>
              <div className="serif" style={{ fontSize: 30, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>The Debt Program</div>
              <div className="mono" style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: BLUE, fontWeight: 700, marginTop: 12 }}>Inscripción directa · desde $405 USD · cupo limitado</div>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Feat>Acompañamiento en vivo con Ernesto</Feat>
                <Feat>Data room + modelo + teaser armados</Feat>
                <Feat>Comités de crédito simulados</Feat>
                <Feat>Presentación ante red de +60 fondos</Feat>
              </div>
              <div style={{ marginTop: 18 }}>
                <LinkCTA href="/programas" testid="cta-program" variant="ink" big>Ver cohortes e inscribirme →</LinkCTA>
              </div>
            </div>

            {/* Capital Markets as a Service / 1:1 */}
            <div style={ladderCard("#FFFFFF", "#141414")}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: BLUE, fontWeight: 700 }}>1:1 · a la medida</div>
              <div className="serif" style={{ fontSize: 30, marginTop: 10, letterSpacing: "-0.02em", lineHeight: 1 }}>Capital Markets as a Service</div>
              <div className="mono" style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", color: BLUE, fontWeight: 700, marginTop: 12 }}>Retainer + éxito · cotización a la medida</div>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Feat>Diseño de la estructura óptima de capital</Feat>
                <Feat>Negociación de term sheets y convenios</Feat>
                <Feat>Ejecución del fondeo hasta el desembolso</Feat>
                <Feat>Directo con mi firma, ERGO Capital</Feat>
              </div>
              <div style={{ marginTop: 18 }}>
                <ApplyCTA applyKey="apply_1to1" formHref="#apply" testid="cta-cmaas" variant="cobalt" big>Hablemos de tu mandato →</ApplyCTA>
              </div>
            </div>
          </div>
          </div>
        </div>

      </div>
    </div>
    <style>{`
      .maca-cta { transition: transform 0.08s ease, box-shadow 0.08s ease; }
      .maca-cta:hover { transform: translate(-1px, -1px); box-shadow: 6px 6px 0 #141414; }
      .maca-cta:active { transform: translate(3px, 3px); box-shadow: 1px 1px 0 #141414; }
      @media (max-width: 720px) {
        [data-testid="${testid}"] .ladder-free-grid { grid-template-columns: 1fr !important; }
        [data-testid="${testid}"] .ladder-circle-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 11. FAQ SECTION — accordion
// ============================================================================
const FAQ_ITEMS = [
  {
    q: "¿En qué se diferencia esto de una aceleradora como YC?",
    a: "Las aceleradoras son generalistas y viven del equity. Yo estoy 100% enfocado en estructurar capital y deuda. Aquí aprendes (o yo lo hago por ti) a levantar sin regalar tu empresa, con una red de +60 fondos de crédito — no de VCs.",
  },
  {
    q: "No soy fintech. ¿Igual me sirve?",
    a: "Sí. Trabajo con fundadores de startup levantando su primera ronda, PyMEs buscando capital de trabajo, plataformas de crédito escalando su warehouse y fondos buscando pipeline. Elige tu lado y listo.",
  },
  {
    q: "¿Cuánto cuesta y cómo empiezo?",
    a: "Gratis con el newsletter. El Toolkit (modelos y plantillas) cuesta $149. Los ebooks arrancan en $19 y los cursos por vertical desde $149. La membresía The Debt Circle es de $79/mes. Y si quieres la certificación avalada por IADA Anáhuac o entrar al cohorte, aplicas y platicamos.",
  },
  {
    q: "¿Me garantizas que cierro mi ronda?",
    a: "No — el que te lo garantice te está mintiendo. Lo que sí te garantizo: 14 días de reembolso en cualquier producto, y en el mandato 1:1 trabajo tu deal contigo hasta que tengas un term sheet real sobre la mesa.",
  },
  {
    q: "¿Ya tengo CFO o equipo. Aún me sirve?",
    a: "Claro. A tu equipo le van a encantar las plantillas. Yo los acelero, no los reemplazo. Y si necesitas manos extra, en el 1:1 o en el mandato entro directo contigo.",
  },
  {
    q: "¿En qué idioma está todo?",
    a: "En español. Estoy enfocado en LATAM y en el mercado hispano de USA. Las plantillas también las tienes en inglés por si negocias con contrapartes gringas.",
  },
  {
    q: "¿Qué es exactamente \"Yo te la consigo\"?",
    a: "Es un mandato con mi firma, ERGO Capital. Yo estructuro y negocio tu ronda o tu deuda por ti, con acceso a mi red de fondos. Tú firmas, yo hago el trabajo pesado. Aplica y platicamos si tu caso encaja.",
  },
];

export const FAQSection = ({ testid = "faq" }) => {
  const [open, setOpen] = useState(0);
  return (
    <section
      data-testid={testid}
      id="faq"
      style={{ padding: "120px 40px", position: "relative", zIndex: 5, borderTop: "1px solid rgba(15,56,44,0.06)" }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
          — PREGUNTAS QUE TODOS ME HACEN
        </div>
        <h2 className="serif" style={{ fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300, letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
          Léelo <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>antes</span> de aplicar.
        </h2>
        <div style={{ marginTop: 50 }}>
          {FAQ_ITEMS.map((f, i) => (
            <div key={i} data-testid={`${testid}-item-${i}`} style={{ borderTop: "1px solid rgba(15,56,44,0.08)" }}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`${testid}-item-${i}-toggle`}
                style={{
                  width: "100%", padding: "26px 0", background: "transparent",
                  border: "none", cursor: "pointer", display: "flex",
                  justifyContent: "space-between", alignItems: "center",
                  color: CREAM, textAlign: "left", fontFamily: "'Archivo', sans-serif",
                }}
              >
                <span style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.35, paddingRight: 20 }}>{f.q}</span>
                <span className="serif" style={{ fontSize: 32, color: GOLD, fontWeight: 300, transition: "transform 0.3s ease", transform: open === i ? "rotate(45deg)" : "rotate(0)", flexShrink: 0, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>+</span>
              </button>
              <div
                data-testid={`${testid}-item-${i}-body`}
                style={{
                  maxHeight: open === i ? 500 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.4s ease, padding 0.3s ease",
                  paddingBottom: open === i ? 26 : 0,
                }}
              >
                <p className="serif" style={{ fontSize: 17, lineHeight: 1.6, opacity: 0.82, fontStyle: "normal", maxWidth: 720, margin: 0 }}>
                  {f.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// ============================================================================
// 12. UNIFIED APPLY FORM — with audience radio selector
// ============================================================================
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { key: "startups",     label: "Fundador de startup", sub: "Pre-seed → Serie A · levantando equity + tu primera línea de deuda" },
  { key: "smbs",         label: "PyME",     sub: "Negocio con facturación real levantando capital de trabajo o crédito" },
  { key: "lenders",      label: "Plataforma de crédito / prestamista", sub: "Fintech, SOFOM o fondo de crédito escalando un warehouse" },
  { key: "institutions", label: "Institución financiera",    sub: "Fondo, family office o banco buscando pipeline" },
];

export const ApplyForm = ({ testid = "apply", defaultAudience }) => {
  const [aud, setAud] = useState(defaultAudience || "startups");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // Read hash on mount and when it changes to preselect audience.
  // Also honors localStorage.tcg_track set by the ExitIntentPopup quiz.
  useEffect(() => {
    const applyHash = () => {
      const h = window.location.hash;
      if (h) {
        if (h.includes("startups")) return setAud("startups");
        if (h.includes("smbs")) return setAud("smbs");
        if (h.includes("lenders")) return setAud("lenders");
        if (h.includes("institutions")) return setAud("institutions");
      }
      // Fallback: persisted choice from exit-intent quiz
      try {
        const t = localStorage.getItem("tcg_track");
        if (t && ["startups", "smbs", "lenders", "institutions"].includes(t)) setAud(t);
      } catch {}
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Route to correct backend endpoint per audience
      const endpoints = {
        startups:     { url: "/startups/apply",  body: { founder_name: name, founder_email: email, company_name: company, notes, tier: "cohort" } },
        smbs:         { url: "/smb/apply",       body: { founder_name: name, founder_email: email, company_name: company, why_now: notes, lang: "en" } },
        lenders:      { url: "/application",     body: { founder_name: name, founder_email: email, company_name: company, notes, raise_size: "" } },
        institutions: { url: "/allocator/apply", body: { contact_name: name, contact_email: email, firm_name: company, notes } },
      };
      const cfg = endpoints[aud];
      await axios.post(`${API}${cfg.url}`, cfg.body);
      setDone(true);
    } catch {
      setErr("Algo salió mal. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid={testid}
      id="apply"
      style={{ padding: "120px 40px", background: BG_ALT, position: "relative", zIndex: 5, borderTop: `1px solid ${GOLD}22` }}
    >
      {/* Anchor targets for each audience */}
      <span id="apply-startups" style={{ position: "absolute", top: -80 }} />
      <span id="apply-smbs" style={{ position: "absolute", top: -80 }} />
      <span id="apply-lenders" style={{ position: "absolute", top: -80 }} />
      <span id="apply-institutions" style={{ position: "absolute", top: -80 }} />

      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
          — APLICA · UN FORMULARIO · CUALQUIER PERFIL
        </div>
        <h2 className="serif" style={{ fontSize: "clamp(34px, 4.5vw, 68px)", fontWeight: 300, letterSpacing: "-0.04em", lineHeight: 1, margin: 0, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
          Dime <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>quién eres.</span>
        </h2>
        <p className="serif" style={{ marginTop: 22, fontSize: 18, lineHeight: 1.55, opacity: 0.78, fontStyle: "normal", maxWidth: 720 }}>
          Leo cada aplicación yo mismo. Respondo en 48 horas, garantizado. Si no encajas, te lo digo directo — y te apunto a dónde sí.
        </p>

        {done ? (
          <div data-testid={`${testid}-success`} style={{ marginTop: 50, padding: 48, background: BG, border: `1px solid ${GOLD}66`, textAlign: "center" }}>
            <div className="serif" style={{ fontSize: 60, color: GOLD, fontWeight: 300, fontStyle: "normal", lineHeight: 1, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>Recibido.</div>
            <p className="serif" style={{ marginTop: 20, fontSize: 20, lineHeight: 1.5, fontStyle: "normal", opacity: 0.85 }}>
              Lo leo en las próximas 48 horas y te respondo yo mismo.
            </p>
            <p className="mono" style={{ marginTop: 20, fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.55 }}>
              — Ernesto Gómez Berjón
            </p>
          </div>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 50 }} data-testid={`${testid}-form`}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase", marginBottom: 16 }}>
              01 · Soy...
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }} className="apply-radio-grid">
              {AUDIENCES.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setAud(a.key)}
                  data-testid={`${testid}-audience-${a.key}`}
                  style={{
                    padding: "18px 22px", textAlign: "left", cursor: "pointer",
                    background: aud === a.key ? `${GOLD}0d` : "transparent",
                    border: aud === a.key ? `1.5px solid ${GOLD}` : `1px solid ${CREAM}22`,
                    color: CREAM, fontFamily: "'Archivo', sans-serif",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ fontSize: 15.5, fontWeight: 500, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: "50%",
                      border: `1.5px solid ${aud === a.key ? GOLD : CREAM + "55"}`,
                      background: aud === a.key ? GOLD : "transparent",
                      flexShrink: 0, transition: "all 0.2s ease",
                    }} />
                    {a.label}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.65, marginTop: 6, marginLeft: 24, lineHeight: 1.4 }}>{a.sub}</div>
                </button>
              ))}
            </div>

            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase", marginTop: 40, marginBottom: 16 }}>
              02 · Lo básico
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="apply-grid-2">
              <input
                type="text" required placeholder="Nombre completo"
                value={name} onChange={(e) => setName(e.target.value)}
                data-testid={`${testid}-name`}
                style={{ padding: 18, background: BG, border: `1px solid ${CREAM}22`, color: CREAM, fontFamily: "'Archivo', sans-serif", fontSize: 15, outline: "none" }}
              />
              <input
                type="email" required placeholder="Correo de trabajo"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={`${testid}-email`}
                style={{ padding: 18, background: BG, border: `1px solid ${CREAM}22`, color: CREAM, fontFamily: "'Archivo', sans-serif", fontSize: 15, outline: "none" }}
              />
            </div>
            <input
              type="text" required placeholder={aud === "institutions" ? "Nombre del fondo" : "Nombre de la empresa"}
              value={company} onChange={(e) => setCompany(e.target.value)}
              data-testid={`${testid}-company`}
              style={{ width: "100%", padding: 18, background: BG, border: `1px solid ${CREAM}22`, color: CREAM, fontFamily: "'Archivo', sans-serif", fontSize: 15, outline: "none", marginTop: 12 }}
            />
            <textarea
              placeholder="Un párrafo sobre dónde estás hoy y para qué necesitas capital. Entre más específico, más rápido te respondo."
              rows={5}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid={`${testid}-notes`}
              style={{ width: "100%", padding: 18, background: BG, border: `1px solid ${CREAM}22`, color: CREAM, fontFamily: "'Archivo', sans-serif", fontSize: 15, outline: "none", marginTop: 12, resize: "vertical", lineHeight: 1.55 }}
            />

            {err && <div data-testid={`${testid}-err`} style={{ color: "#F97316", fontSize: 13.5, marginTop: 16 }}>{err}</div>}

            <div style={{ marginTop: 32, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="submit" disabled={busy}
                data-testid={`${testid}-submit`}
                className="btn-primary"
                style={{
                  padding: "18px 36px", fontFamily: "'Archivo', sans-serif",
                  fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
                  background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414", border: "none", cursor: "pointer",
                  fontWeight: 700, opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? "enviando..." : "enviar mi aplicación →"}
              </button>
              <div className="mono" style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.5 }}>
                respondo en 48h · sin bots
              </div>
            </div>
          </form>
        )}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .apply-radio-grid { grid-template-columns: 1fr !important; }
          .apply-grid-2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};



// ============================================================================
// 13. EXIT INTENT POPUP — Bloomberg-terminal aesthetic · 2-step quiz
//     Step 1: "Which best describes you?" (4 audience cards)
//     Step 2: Personalized offer + email capture
//     On submit: scroll to #apply-<track> to pre-select the ApplyForm audience.
//     Triggers on mouseleave (desktop) or 45s idle after 40% scroll (mobile).
// ============================================================================
const EXIT_TRACKS = {
  startups: {
    label: "Fundador de startup",
    sub: "Pre-seed → Serie A · levantando equity + tu primera deuda",
    kicker: "— PARA FUNDADORES DE STARTUP",
    gift: "El Módulo 1 + el cheatsheet SAFE-a-Deuda",
    line: "La escalera exacta que usamos para mantener tu cap table bajo 15% de dilución hasta Serie A — aunque levantes $10M en deuda.",
    bullets: [
      "Las 5 fuentes de capital · cuál va en pre-seed vs Serie A",
      "Cuándo un SAFE te ahorra 15% de dilución (y cuándo te cuesta)",
      "El script de negociación de bridge que uso",
      "Chequeo de cap table · autoauditoría de 12 puntos",
    ],
    ctaLabel: "Enviarme el kit de fundador",
    successCta: "Ver el track de startups →",
    anchor: "#apply-startups",
  },
  smbs: {
    label: "PyME",
    sub: "Negocio con facturación real · capital de trabajo o línea de crédito",
    kicker: "— PARA PYMES DE HABLA HISPANA",
    gift: "Módulo 1 + el Kit del Crédito Bancario MX",
    line: "El manual que le pasamos a nuestras PyMEs para dejar de pagar factoring al 40% APR y estructurar una línea bancaria real en 90 días.",
    bullets: [
      "Cómo negociar con BBVA, Kapital y BNext (script incluido)",
      "Factoring vs warehouse · por qué pagas 3× más de lo necesario",
      "Modelo simple: el costo real del capital que estás usando",
      "Los 4 documentos que todo banco te pide (y nadie te dice)",
    ],
    ctaLabel: "Enviarme el kit",
    successCta: "Ver el track de PyMEs →",
    anchor: "#apply-smbs",
  },
  lenders: {
    label: "Plataforma de crédito / prestamista",
    sub: "Fintech, SOFOM o fondo de crédito escalando un warehouse",
    kicker: "— PARA PLATAFORMAS DE CRÉDITO",
    gift: "El Módulo 1 + la plantilla de Warehouse Term Sheet",
    line: "La plantilla de term sheet redlineada que usa nuestro grupo para cerrar facilities de $5M–$50M — con la taxonomía de covenants que todo fondo te pide.",
    bullets: [
      "Estructura de warehouse $5M–$50M · pre-negociada",
      "Taxonomía de covenants · qué pelear y qué ceder",
      "Formato loan tape v3.2 (el que aceptan +60 fondos)",
      "Las 12 preguntas que te van a hacer los fondos · textuales",
    ],
    ctaLabel: "Enviarme el kit de prestamista",
    successCta: "Aplicar →",
    anchor: "#apply-lenders",
  },
  institutions: {
    label: "Institución financiera",
    sub: "Fondo, family office o banco buscando pipeline",
    kicker: "— PARA FONDOS Y FAMILY OFFICES",
    gift: "Acceso al deal-flow curado · 12-15 empresas / grupo",
    line: "Un memo trimestral con plataformas de crédito ya analizadas, loan tapes estandarizados y match por tesis. Gratis para fondos verificados.",
    bullets: [
      "Loan tape estandarizado · tu equipo se ahorra 40+ hrs por deal",
      "Pipeline pre-analizado · cada ticket revisado por ERGO",
      "Match por asset class · tamaño de ticket · tesis · geografía",
      "Primer cierre en 3 semanas · no en meses",
    ],
    ctaLabel: "Pedir acceso de fondo",
    successCta: "Ver el track de instituciones →",
    anchor: "#apply-institutions",
  },
};

const EXIT_TRACK_ICONS = {
  startups: (c) => (
    <svg width="26" height="26" viewBox="0 0 64 64" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 8 52 L 22 38 L 32 44 L 56 20" /><path d="M 44 20 L 56 20 L 56 32" /><line x1="8" y1="58" x2="56" y2="58" />
    </svg>
  ),
  smbs: (c) => (
    <svg width="26" height="26" viewBox="0 0 64 64" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 8 22 L 12 12 L 52 12 L 56 22" /><line x1="6" y1="22" x2="58" y2="22" /><path d="M 10 22 L 10 54 L 54 54 L 54 22" /><path d="M 25 54 L 25 38 L 39 38 L 39 54" />
    </svg>
  ),
  lenders: (c) => (
    <svg width="26" height="26" viewBox="0 0 64 64" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="10" y="32" width="10" height="22" /><rect x="27" y="18" width="10" height="36" /><rect x="44" y="26" width="10" height="28" /><line x1="8" y1="54" x2="56" y2="54" />
    </svg>
  ),
  institutions: (c) => (
    <svg width="26" height="26" viewBox="0 0 64 64" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M 6 16 L 32 6 L 58 16" /><line x1="6" y1="20" x2="58" y2="20" /><line x1="14" y1="24" x2="14" y2="48" /><line x1="26" y1="24" x2="26" y2="48" /><line x1="38" y1="24" x2="38" y2="48" /><line x1="50" y1="24" x2="50" y2="48" /><line x1="6" y1="52" x2="58" y2="52" />
    </svg>
  ),
};

export const ExitIntentPopup = ({ testid = "exit-intent" }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);         // 1 = quiz, 2 = offer
  const [track, setTrack] = useState(null);    // startups | smbs | lenders | institutions
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [seatsLeft, setSeatsLeft] = useState(4);
  const shownRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("tcg_exit_shown") === "1") {
      shownRef.current = true;
      return;
    }

    const grace = setTimeout(() => {
      const trigger = () => {
        if (shownRef.current) return;
        shownRef.current = true;
        sessionStorage.setItem("tcg_exit_shown", "1");
        setOpen(true);
      };

      const onMouseLeave = (e) => {
        if (e.clientY <= 0 && !shownRef.current) trigger();
      };
      document.addEventListener("mouseleave", onMouseLeave);

      timerRef.current = setInterval(() => {
        if (shownRef.current) return;
        const scrolled = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        if (scrolled > 0.4) trigger();
      }, 45000);

      let lastY = window.scrollY;
      let upDistance = 0;
      const onScroll = () => {
        const dy = window.scrollY - lastY;
        if (dy < 0) upDistance += -dy;
        else upDistance = 0;
        lastY = window.scrollY;
        if (upDistance > 900 && window.scrollY < 400 && !shownRef.current) trigger();
      };
      window.addEventListener("scroll", onScroll, { passive: true });

      return () => {
        document.removeEventListener("mouseleave", onMouseLeave);
        window.removeEventListener("scroll", onScroll);
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }, 8000);

    return () => clearTimeout(grace);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setSeatsLeft((s) => Math.max(2, s - 1)), 22000);
    return () => clearTimeout(t);
  }, [open]);

  const pickTrack = (key) => {
    setTrack(key);
    setStep(2);
    // persist for later personalization across the site
    try { localStorage.setItem("tcg_track", key); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !email.includes("@")) { setErr("Invalid email."); return; }
    setBusy(true);
    try {
      const axios = (await import("axios")).default;
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        source: `exit-intent-${track || "generic"}`,
        lang: "es",
      });
      setDone(true);
    } catch {
      setErr("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => setOpen(false);

  const goToTrack = () => {
    setOpen(false);
    const anchor = track ? EXIT_TRACKS[track].anchor : "#apply";
    // update hash so ApplyForm's hashchange listener preselects the audience
    setTimeout(() => {
      window.location.hash = anchor;
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  if (!open) return null;

  const t = track ? EXIT_TRACKS[track] : null;

  return (
    <div
      data-testid={testid}
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        background: "rgba(10,27,20,0.45)",
        backdropFilter: "blur(6px)",
        animation: "exit-fade 0.35s ease both",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <style>{`
        @keyframes exit-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes exit-slide { from { opacity: 0; transform: translateY(24px) scale(0.98); } to { opacity: 1; transform: none; } }
        @keyframes exit-blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        @keyframes exit-step-in { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: none; } }
      `}</style>

      <div
        style={{
          position: "relative",
          maxWidth: 760, width: "100%",
          background: BG,
          border: `1px solid ${GOLD}`,
          boxShadow: `0 40px 120px ${GOLD}22, 0 0 0 8px rgba(15,56,44,0.10)`,
          animation: "exit-slide 0.5s cubic-bezier(0.16,1,0.3,1) both",
          maxHeight: "92vh", overflow: "auto",
        }}
      >
        {/* Corner brackets */}
        <span style={{ position: "absolute", top: 12, left: 12, width: 16, height: 16, borderTop: `1.5px solid ${GOLD}`, borderLeft: `1.5px solid ${GOLD}` }} />
        <span style={{ position: "absolute", top: 12, right: 12, width: 16, height: 16, borderTop: `1.5px solid ${GOLD}`, borderRight: `1.5px solid ${GOLD}` }} />
        <span style={{ position: "absolute", bottom: 12, left: 12, width: 16, height: 16, borderBottom: `1.5px solid ${GOLD}`, borderLeft: `1.5px solid ${GOLD}` }} />
        <span style={{ position: "absolute", bottom: 12, right: 12, width: 16, height: 16, borderBottom: `1.5px solid ${GOLD}`, borderRight: `1.5px solid ${GOLD}` }} />

        {/* Terminal header — with step indicator */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 28px", borderBottom: `1px solid ${GOLD}22`,
          background: BG_ALT, gap: 14,
        }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase" }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#FF6B35", marginRight: 10, verticalAlign: "middle", animation: "exit-blink 1.4s infinite" }} />
            {done ? "LISTO · REVISA TU CORREO" : step === 1 ? "ESPERA · QUIZ DE 30 SEGUNDOS" : "PASO 02 · TU OFERTA"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!done && (
              <div className="mono" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span data-testid={`${testid}-step-dot-1`} style={{ width: 22, height: 3, background: step >= 1 ? GOLD : `${CREAM}22` }} />
                <span data-testid={`${testid}-step-dot-2`} style={{ width: 22, height: 3, background: step >= 2 ? GOLD : `${CREAM}22` }} />
              </div>
            )}
            <button
              type="button"
              onClick={close}
              data-testid={`${testid}-close`}
              aria-label="Close"
              style={{
                width: 30, height: 30, background: "transparent",
                border: `1px solid ${CREAM}22`, color: CREAM, opacity: 0.65,
                cursor: "pointer", fontSize: 16, lineHeight: 1,
              }}
            >×</button>
          </div>
        </div>

        {done ? (
          /* ===== SUCCESS STATE ===== */
          <div data-testid={`${testid}-success`} style={{ padding: "48px 44px", textAlign: "center" }}>
            <div className="serif" style={{ fontSize: 64, color: GOLD, fontWeight: 300, fontStyle: "normal", lineHeight: 1, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
              Enviado.
            </div>
            <p className="serif" style={{ marginTop: 18, fontSize: 18, lineHeight: 1.5, fontStyle: "normal", opacity: 0.9, maxWidth: 500, marginLeft: "auto", marginRight: "auto" }}>
              {t.gift} va en camino a <span style={{ color: GOLD }}>{email}</span>.
            </p>
            <p className="serif" style={{ marginTop: 8, fontSize: 15, lineHeight: 1.55, fontStyle: "normal", opacity: 0.7, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
              Mientras llega — ¿te saltas la fila y aplicas al Grupo 03? Solo quedan <span style={{ color: GOLD, fontWeight: 600 }}>{seatsLeft} de 12</span> lugares.
            </p>
            <div style={{ marginTop: 30, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={goToTrack}
                data-testid={`${testid}-success-cta`}
                style={{
                  padding: "16px 26px", background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414", border: "none", cursor: "pointer",
                  fontFamily: "'Archivo', sans-serif", fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 700,
                }}
              >
                {t.successCta}
              </button>
              <button
                type="button"
                onClick={close}
                data-testid={`${testid}-success-close`}
                className="mono"
                style={{
                  padding: "16px 22px", background: "transparent", color: CREAM,
                  border: `1px solid ${CREAM}33`, cursor: "pointer",
                  fontSize: 10.5, letterSpacing: "0.24em", textTransform: "uppercase",
                }}
              >
                Seguir viendo →
              </button>
            </div>
          </div>
        ) : step === 1 ? (
          /* ===== STEP 1 · QUIZ ===== */
          <div data-testid={`${testid}-step-1`} style={{ padding: "40px 44px 40px", animation: "exit-step-in 0.35s ease both" }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", color: CREAM, opacity: 0.6, textTransform: "uppercase", marginBottom: 16 }}>
              — 30 segundos · oferta personalizada al final
            </div>
            <h3
              className="serif"
              data-testid={`${testid}-quiz-title`}
              style={{
                fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 300, lineHeight: 1.02,
                letterSpacing: "-0.035em", margin: 0, color: CREAM,
                fontVariationSettings: '"SOFT" 30, "opsz" 144',
              }}
            >
              ¿Cuál te describe <br/>
              <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
                mejor?
              </span>
            </h3>
            <p className="serif" style={{ marginTop: 14, fontSize: 15, lineHeight: 1.5, fontStyle: "normal", opacity: 0.72, maxWidth: 520 }}>
              Elige una — te mando el kit de plantillas exacto para tu lado de la mesa.
            </p>

            <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="exit-quiz-grid">
              {Object.entries(EXIT_TRACKS).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickTrack(key)}
                  data-testid={`${testid}-quiz-${key}`}
                  className="exit-quiz-card"
                  style={{
                    padding: "18px 20px", textAlign: "left", cursor: "pointer",
                    background: BG_ALT,
                    border: `1px solid ${CREAM}22`,
                    color: CREAM, fontFamily: "'Archivo', sans-serif",
                    transition: "border-color 0.2s ease, background 0.2s ease, transform 0.2s ease",
                    display: "flex", gap: 14, alignItems: "flex-start",
                  }}
                >
                  <div style={{
                    width: 42, height: 42, flexShrink: 0,
                    border: `1px solid ${GOLD}44`, background: BG,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {EXIT_TRACK_ICONS[key](GOLD)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: "-0.005em", color: CREAM, lineHeight: 1.2 }}>
                      {cfg.label}
                    </div>
                    <div style={{ fontSize: 11.5, opacity: 0.62, marginTop: 5, lineHeight: 1.4 }}>{cfg.sub}</div>
                    <div className="mono" style={{ marginTop: 10, fontSize: 9, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase" }}>
                      elegir esto →
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mono" style={{
              marginTop: 20, fontSize: 9.5, letterSpacing: "0.24em",
              textTransform: "uppercase", opacity: 0.5, textAlign: "center",
            }}>
              <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "#FF6B35", marginRight: 8, verticalAlign: "middle", animation: "exit-blink 1.4s infinite" }} />
              Grupo 03 · quedan {seatsLeft} de 12 lugares
            </div>

            <style>{`
              .exit-quiz-card:hover {
                border-color: ${GOLD} !important;
                background: ${BG_ELEV} !important;
                transform: translateY(-2px);
              }
              @media (max-width: 640px) {
                [data-testid="${testid}"] .exit-quiz-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </div>
        ) : (
          /* ===== STEP 2 · PERSONALIZED OFFER ===== */
          <div data-testid={`${testid}-step-2`} style={{ padding: "36px 44px 40px", animation: "exit-step-in 0.35s ease both" }}>
            {/* Back arrow */}
            <button
              type="button"
              onClick={() => setStep(1)}
              data-testid={`${testid}-back`}
              className="mono"
              style={{
                background: "transparent", border: "none",
                color: CREAM, opacity: 0.55, cursor: "pointer",
                fontSize: 9.5, letterSpacing: "0.24em", textTransform: "uppercase",
                padding: 0, marginBottom: 14,
              }}
            >
              ← Atrás
            </button>

            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", color: GOLD, textTransform: "uppercase", marginBottom: 14 }}>
              {t.kicker}
            </div>

            <h3
              className="serif"
              data-testid={`${testid}-title`}
              style={{
                fontSize: "clamp(26px, 3.6vw, 40px)", fontWeight: 300,
                lineHeight: 1.05, letterSpacing: "-0.03em",
                margin: 0, color: CREAM,
                fontVariationSettings: '"SOFT" 30, "opsz" 144',
              }}
            >
              <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
                {t.gift}
              </span>
              <br />va por la casa.
            </h3>

            <p className="serif" style={{ marginTop: 14, fontSize: 15.5, lineHeight: 1.55, fontStyle: "normal", opacity: 0.82, maxWidth: 560 }}>
              {t.line}
            </p>

            <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px" }} className="exit-lessons">
              {t.bullets.map((b, i) => (
                <div key={i} className="mono" style={{ fontSize: 11, letterSpacing: "0.04em", color: CREAM, opacity: 0.85, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ color: GOLD, fontSize: 10, lineHeight: "16px" }}>✓</span>
                  <span style={{ fontSize: 12.5, lineHeight: 1.4 }}>{b}</span>
                </div>
              ))}
            </div>

            <form onSubmit={submit} data-testid={`${testid}-form`} style={{ marginTop: 26, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                type="email" required placeholder={track === "smbs" ? "tu@empresa.com" : "tu@correo.com"}
                value={email} onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                data-testid={`${testid}-input`}
                style={{
                  flex: "1 1 240px", minWidth: 200,
                  padding: "16px 18px", background: BG_ALT,
                  border: `1px solid ${CREAM}22`, color: CREAM,
                  fontFamily: "'Archivo', sans-serif", fontSize: 15, outline: "none",
                }}
                onFocus={(e) => { e.target.style.borderColor = GOLD; }}
                onBlur={(e) => { e.target.style.borderColor = `${CREAM}22`; }}
              />
              <button
                type="submit" disabled={busy}
                data-testid={`${testid}-submit`}
                style={{
                  padding: "16px 24px", background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414",
                  border: "none", cursor: busy ? "wait" : "pointer",
                  fontFamily: "'Archivo', sans-serif", fontSize: 11,
                  letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700,
                  opacity: busy ? 0.7 : 1, whiteSpace: "nowrap",
                }}
              >
                {busy ? "enviando..." : `${t.ctaLabel} →`}
              </button>
            </form>

            {err && <div data-testid={`${testid}-err`} style={{ color: "#F97316", fontSize: 12.5, marginTop: 12 }}>{err}</div>}

            <div style={{
              marginTop: 22, padding: "12px 16px",
              background: BG_ALT, border: `1px solid ${GOLD}33`,
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            }}>
              <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#FF6B35", animation: "exit-blink 1.2s infinite", flexShrink: 0 }} />
              <span className="mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: CREAM, opacity: 0.82 }}>
                Grupo 03 · <span style={{ color: GOLD, fontWeight: 700 }}>quedan {seatsLeft} de 12 lugares</span>
              </span>
            </div>

            <div className="mono" style={{
              marginTop: 14, fontSize: 9.5, letterSpacing: "0.24em",
              textTransform: "uppercase", opacity: 0.5,
            }}>
              gratis para siempre · cancela cuando quieras · cero spam
            </div>

            <style>{`
              @media (max-width: 640px) {
                [data-testid="${testid}-step-2"] .exit-lessons {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// 14. SCARCITY BAR — animated seats-remaining progress with pulsing dot
// ============================================================================
export const ScarcityBar = ({
  filled = 8,
  total = 12,
  label = "Grupo 03 · Inscripciones abiertas",
  deadline = closeLabelCK(),
  testid = "scarcity",
}) => {
  const parts = label.split("·");
  const title = (parts[0] || "Grupo 03").trim();
  const subtitle = (parts.slice(1).join("·") || "Inscripciones abiertas").trim();
  return (
    <section data-testid={testid} style={{ padding: "10px 40px", maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 5 }}>
      <div className="scar-grid" style={{ background: "#FFFFFF", borderRadius: 26, boxShadow: "0 20px 50px rgba(20,20,20,0.12)", border: "1px solid rgba(20,20,20,0.06)", overflow: "hidden", position: "relative", display: "grid", gridTemplateColumns: "1fr 300px", alignItems: "stretch" }}>
        <div style={{ padding: "34px 38px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span className="mk-livedot" style={{ width: 9, height: 9, borderRadius: "50%", background: "#FF5A1F", flexShrink: 0 }} />
            <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "#FF5A1F" }}>Live</span>
            <span style={{ width: 1, height: 16, background: "rgba(20,20,20,0.2)" }} />
            <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD }}>Lugares llenándose</span>
          </div>
          <h3 style={{ margin: "16px 0 0", fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(34px, 4.4vw, 52px)", letterSpacing: "-0.03em", lineHeight: 0.95, color: "#141414", display: "inline-block" }}>
            <Highlight>{title}</Highlight>
          </h3>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: "-0.02em", color: "#141414", marginTop: 8 }}>{subtitle}</div>

          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginTop: 20 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "#141414" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill={GOLD}><circle cx="9" cy="8" r="3.2"/><path d="M3 20a6 6 0 0 1 12 0z"/><circle cx="17" cy="9" r="2.6" opacity="0.7"/><path d="M14.5 20a5 5 0 0 1 7.5-2.6V20z" opacity="0.7"/></svg>
              {filled} ocupados · <span style={{ color: GOLD }}>{total - filled} libres</span>
            </span>
            <span style={{ width: 1, height: 18, background: "rgba(20,20,20,0.15)" }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Archivo', sans-serif", fontWeight: 600, fontSize: 12.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "#141414", opacity: 0.7 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#141414" strokeWidth="2"><rect x="3" y="4.5" width="18" height="17" rx="2"/><path d="M3 9h18M8 2.5v4M16 2.5v4"/></svg>
              {deadline}
            </span>
          </div>

          <div style={{ marginTop: 18, display: "flex", gap: 4 }} data-testid={`${testid}-progress`}>
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} style={{ flex: 1, height: 12, borderRadius: 4, background: i < filled ? `hsl(${250 - (i / total) * 230}, 85%, 58%)` : "rgba(20,20,20,0.08)" }} />
            ))}
          </div>

          <div style={{ marginTop: 22 }}>
            <PillCTA href="#pricing" variant="lime" leadingArrow arrow={false} testid={`${testid}-cta`}>Ver precios y apartar</PillCTA>
          </div>
        </div>

        <div className="scar-art" style={{ position: "relative", minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          <div style={{ position: "absolute", right: "-40%", top: 0, bottom: 0, width: "170%", background: COBALT, clipPath: "polygon(28% 0, 100% 0, 100% 100%, 6% 100%)", opacity: 0.95 }} />
          <div style={{ position: "relative", zIndex: 2 }}>
            <GlassArt accent={COBALT} glyphColor={COBALT} glyph="users" width={230} height={220} />
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 820px) {
          [data-testid="${testid}"] .scar-grid { grid-template-columns: 1fr !important; }
          [data-testid="${testid}"] .scar-art { display: none !important; }
        }
      `}</style>
    </section>
  );
};


// ============================================================================
// 15. MID-SCROLL CTA — dark strip with headline + button, sits between sections
// ============================================================================
export const MidScrollCTA = ({
  kicker = "— CUANDO QUIERAS",
  headline,
  subline,
  ctaText = "Ver precios",
  ctaHref = "#pricing",
  secondaryText,
  secondaryHref,
  variant = "dark",
  testid = "mid-cta",
  note = "Acceso inmediato tras el pago.",
}) => {
  return (
    <section data-testid={testid} style={{ padding: "70px 40px", position: "relative", zIndex: 5, overflow: "hidden" }}>
      <div style={{ position: "absolute", right: -90, bottom: -110, width: 320, height: 260, background: COBALT, borderRadius: "50%", opacity: 0.9, zIndex: 0 }} />
      <div className="mid-cta-grid" style={{ maxWidth: 1160, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 330px", gap: 40, alignItems: "center", position: "relative", zIndex: 2 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 26, height: 2, background: GOLD }} />
            <span className="mono" style={{ fontSize: 11, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", fontWeight: 700 }}>{String(kicker).replace(/^—\s*/, "")}</span>
          </div>
          <h3 data-testid={`${testid}-headline`} style={{ margin: "18px 0 0", fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(34px, 4.6vw, 60px)", letterSpacing: "-0.035em", lineHeight: 0.98, color: "#141414" }}>
            {headline}
          </h3>
          {subline && <p style={{ marginTop: 20, fontFamily: "'Archivo', sans-serif", fontSize: 19, lineHeight: 1.5, opacity: 0.72, maxWidth: 520 }}>{subline}</p>}
          <div style={{ marginTop: 28, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <PillCTA href={ctaHref} variant="lime" bolt testid={`${testid}-cta`} big>{ctaText}</PillCTA>
            {secondaryText && secondaryHref && <PillCTA href={secondaryHref} variant="ghost" testid={`${testid}-secondary`} big>{String(secondaryText).replace(/\s*→\s*$/, "")}</PillCTA>}
          </div>
          <Scribble arrow="none" size={20} style={{ marginTop: 16, marginLeft: 8 }}>{note}</Scribble>
        </div>
        <div className="mid-cta-art" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <GlassArt accent={COBALT} glyphColor={COBALT} glyph="chart" width={320} height={300} />
        </div>
      </div>
      <style>{`
        @media (max-width: 820px) {
          [data-testid="${testid}"] .mid-cta-grid { grid-template-columns: 1fr !important; }
          [data-testid="${testid}"] .mid-cta-art { display: none !important; }
        }
      `}</style>
    </section>
  );
};


// ============================================================================
// 16. RECENT ACTIVITY — live-updating "someone just applied" social proof
// ============================================================================
const ACTIVITY_ITEMS = [
  { name: "Diego M.", where: "CDMX",       act: "aplicó al Programa",            t: "hace 2m" },
  { name: "Fernanda R.", where: "Nueva York",act: "descargó el Módulo 1",        t: "hace 6m" },
  { name: "Sofía A.",   where: "Miami",    act: "pidió acceso a pipeline",       t: "hace 11m" },
  { name: "Carlos T.",  where: "Bogotá",   act: "aplicó al Programa",            t: "hace 18m" },
  { name: "Alejandra V.",where: "Monterrey",act: "agendó una llamada 1:1",       t: "hace 24m" },
  { name: "Miguel P.",  where: "Lima",     act: "descargó un ebook gratis",      t: "hace 31m" },
  { name: "Ana G.",     where: "Madrid",   act: "descargó el Módulo 1",          t: "hace 44m" },
  { name: "Rodrigo E.", where: "São Paulo",act: "aplicó al track de PyMEs",      t: "hace 58m" },
];

export const RecentActivity = ({ testid = "recent-activity" }) => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show on md+ screens (avoid mobile clutter)
    if (typeof window === "undefined") return;
    if (window.innerWidth < 900) return;

    // First appearance after 6s
    const first = setTimeout(() => setVisible(true), 6000);
    // Rotate items every 7s while visible
    const rotate = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % ACTIVITY_ITEMS.length);
        setVisible(true);
      }, 500);
    }, 8000);

    return () => { clearTimeout(first); clearInterval(rotate); };
  }, []);

  if (!visible) return null;
  const item = ACTIVITY_ITEMS[idx];

  return (
    <div
      data-testid={testid}
      style={{
        position: "fixed", left: 20, bottom: 20, zIndex: 60,
        maxWidth: 320,
        background: BG,
        border: `1px solid ${GOLD}44`,
        borderLeft: `2px solid ${GOLD}`,
        padding: "12px 16px 12px 14px",
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 20px 60px rgba(15,56,44,0.10)",
        animation: "activity-slide-in 0.5s cubic-bezier(0.16,1,0.3,1) both",
      }}
      key={idx}
    >
      <div style={{
        width: 34, height: 34, borderRadius: "50%",
        background: `linear-gradient(135deg, ${GOLD}, #165B46)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Archivo', sans-serif", fontWeight: 600,
        color: BG, fontSize: 12.5, flexShrink: 0,
      }}>
        {item.name.split(" ").map((s) => s[0]).join("")}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 13, color: CREAM, lineHeight: 1.35 }}>
          <span style={{ fontWeight: 600 }}>{item.name}</span>
          <span style={{ opacity: 0.55 }}> de {item.where}</span>
          <br />
          <span style={{ opacity: 0.8, fontSize: 12.5 }}>{item.act}</span>
        </div>
        <div className="mono" style={{ marginTop: 4, fontSize: 9, letterSpacing: "0.24em", color: GOLD, textTransform: "uppercase" }}>
          <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "#4ADE80", marginRight: 6, verticalAlign: "middle" }} />
          {item.t}
        </div>
      </div>
      <style>{`
        @keyframes activity-slide-in {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};


// ============================================================================
// 17. PRICE INCREASE WARNING — one-line urgency banner
// ============================================================================
export const PriceIncreaseBanner = ({
  currentPrice = "$7,500",
  nextPrice = "$9,500",
  date = "1 de marzo, 2026",
  testid = "price-warn",
}) => (
  <div
    data-testid={testid}
    style={{
      padding: "18px 24px",
      background: LIME,
      border: "3px solid #141414",
      borderRadius: 16,
      boxShadow: "6px 6px 0 #141414",
      display: "flex", alignItems: "center", gap: 16,
      maxWidth: 760, margin: "40px auto 0",
      flexWrap: "wrap", justifyContent: "center",
      textAlign: "center",
    }}
  >
    <span className="mono" style={{
      background: "#141414", color: LIME, border: "2px solid #141414", borderRadius: 9,
      boxShadow: "3px 3px 0 rgba(20,20,20,0.25)", padding: "7px 13px", fontSize: 10,
      letterSpacing: "0.18em", fontWeight: 800, textTransform: "uppercase",
      transform: "rotate(-3deg)", display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0,
    }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#FF6B35", boxShadow: "0 0 0 3px rgba(255,107,53,0.35)", animation: "scarcity-pulse 1.4s infinite", display: "inline-block" }} />
      Precio fundador
    </span>
    <span className="mono" style={{ fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "#141414", fontWeight: 700, lineHeight: 1.6 }}>
      Congelado en <span style={{ background: "#FFFFFF", border: "2px solid #141414", borderRadius: 6, padding: "2px 9px", fontWeight: 900 }}>{currentPrice}</span>
      <span style={{ margin: "0 6px", opacity: 0.45 }}>—</span>
      sube a <span style={{ background: "#141414", color: "#FFFFFF", borderRadius: 6, padding: "2px 9px", fontWeight: 900 }}>{nextPrice}</span> el {date}
    </span>
  </div>
);



// ============================================================================
// 18. RATING STAT — big star rating with count-up (Maca-style hero social proof)
// ============================================================================
export const RatingStat = ({
  score = "4.9",
  reviews = 340,
  label = "fundadores formados",
  sublabel = "en 4 tracks · 3 continentes",
  testid = "rating-stat",
}) => {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") { setN(reviews); return; }
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (!el) { setN(reviews); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const dur = 1400;
          const t0 = performance.now();
          const step = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(reviews * eased));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [reviews, testid]);

  return (
    <section
      data-testid={testid}
      style={{
        padding: "70px 40px", position: "relative", zIndex: 5,
        background: BG_ALT, borderTop: `1px solid ${CREAM}0c`, borderBottom: `1px solid ${CREAM}0c`,
      }}
    >
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        display: "grid", gridTemplateColumns: "auto 1fr auto",
        gap: 44, alignItems: "center",
      }} className="rating-grid">
        {/* Score + stars */}
        <div>
          <div className="serif" style={{
            fontSize: "clamp(56px, 8vw, 96px)", fontWeight: 300, color: GOLD,
            letterSpacing: "-0.045em", lineHeight: 0.9,
            fontVariationSettings: '"SOFT" 100, "opsz" 144',
          }}>
            {score}
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 4, alignItems: "center" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill={GOLD}>
                <path d="M12 2 L14.5 8.5 L21.5 9 L16 13.5 L17.8 20.5 L12 16.8 L6.2 20.5 L8 13.5 L2.5 9 L9.5 8.5 Z" />
              </svg>
            ))}
          </div>
        </div>

        {/* Middle: kicker + label */}
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 12 }}>
            — LO QUE DICE EL GRUPO
          </div>
          <h3 className="serif" style={{
            fontSize: "clamp(24px, 2.8vw, 36px)", fontWeight: 300,
            letterSpacing: "-0.028em", lineHeight: 1.1, margin: 0, color: CREAM,
            fontVariationSettings: '"SOFT" 30, "opsz" 144',
          }}>
            <span data-testid={`${testid}-count`} style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
              {n.toLocaleString()}+
            </span>{" "}{label}.
          </h3>
          <p className="mono" style={{ marginTop: 10, fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.55 }}>
            {sublabel}
          </p>
        </div>

        {/* Right: distribution bars */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
          {[
            { stars: 5, pct: 89 },
            { stars: 4, pct: 9 },
            { stars: 3, pct: 1.5 },
            { stars: 2, pct: 0.4 },
            { stars: 1, pct: 0.1 },
          ].map((r) => (
            <div key={r.stars} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }} className="mono">
              <span style={{ opacity: 0.55, minWidth: 12 }}>{r.stars}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill={GOLD} style={{ opacity: 0.75 }}>
                <path d="M12 2 L14.5 8.5 L21.5 9 L16 13.5 L17.8 20.5 L12 16.8 L6.2 20.5 L8 13.5 L2.5 9 L9.5 8.5 Z" />
              </svg>
              <div style={{ flex: 1, height: 4, background: `${CREAM}18`, position: "relative" }}>
                <div style={{ position: "absolute", inset: 0, right: "auto", width: `${r.pct}%`, background: GOLD }} />
              </div>
              <span style={{ opacity: 0.55, minWidth: 30, textAlign: "right", fontSize: 10 }}>{r.pct}%</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          [data-testid="${testid}"] .rating-grid {
            grid-template-columns: 1fr !important;
            text-align: left; gap: 22px !important;
          }
        }
      `}</style>
    </section>
  );
};


// ============================================================================
// 19. FIT FILTER — "Es para ti / No es para ti" qualification (Maca pattern)
// ============================================================================
const FIT_YES = [
  "Vas a levantar equity + tu primera línea de deuda en los próximos 12 meses",
  "Eres una PyME buscando capital de trabajo o crédito bancario",
  "Eres una plataforma de crédito estructurando un warehouse de $5M–$50M",
  "Eres un fondo / family office buscando pipeline ya analizado",
  "Quieres los frameworks que sí usan los equipos de crédito senior",
  "Valoras una hora de mi tiempo en más de $500",
];
const FIT_NO = [
  "Buscas un esquema para hacerte rico rápido o movidas de cripto",
  "Quieres un bootcamp generalista con 200+ personas en un Discord",
  "Tu modelo aún no monetiza y primero necesitas product-market fit",
  "No quieres hacer nada de tarea ni compartir tus números",
  "Estás pre-ingresos y sin una tesis clara de tu plan de capital",
];

export const FitFilter = ({ testid = "fit" }) => (
  <section
    data-testid={testid}
    style={{ padding: "120px 40px", position: "relative", zIndex: 5, borderTop: `1px solid ${CREAM}0c` }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
        — TE LO DIGO CLARO
      </div>
      <h2 className="serif" style={{
        fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300,
        letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, maxWidth: 1000,
        fontVariationSettings: '"SOFT" 30, "opsz" 144',
      }}>
        ¿Esto es <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>para ti</span>?
      </h2>
      <p className="serif" style={{ marginTop: 20, fontSize: 18, lineHeight: 1.55, opacity: 0.75, fontStyle: "normal", maxWidth: 720 }}>
        Prefiero perder la venta antes que sumar a alguien que no encaja. Lee las dos columnas y sé honesto contigo.
      </p>

      <div style={{ marginTop: 60, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }} className="fit-grid">
        {/* YES */}
        <div
          data-testid={`${testid}-yes`}
          style={{
            background: BG_ALT, border: `1px solid ${GOLD}55`,
            borderLeft: `3px solid ${GOLD}`, padding: 40, position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", background: GOLD,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BG} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 12 10 17 20 7" />
              </svg>
            </div>
            <div className="mono" style={{ fontSize: 11, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", fontWeight: 700 }}>
              Esto es para ti si…
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {FIT_YES.map((f, i) => (
              <div key={i} data-testid={`${testid}-yes-${i}`} style={{ display: "flex", gap: 14, alignItems: "flex-start", paddingBottom: i < FIT_YES.length - 1 ? 14 : 0, borderBottom: i < FIT_YES.length - 1 ? `1px solid ${CREAM}0f` : "none" }}>
                <span className="mono" style={{ color: GOLD, fontSize: 12, opacity: 0.9, minWidth: 22, fontWeight: 600 }}>0{i + 1}</span>
                <span style={{ fontSize: 15, lineHeight: 1.5, color: CREAM, opacity: 0.92 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* NO */}
        <div
          data-testid={`${testid}-no`}
          style={{
            background: BG, border: `1px solid ${CREAM}18`,
            borderLeft: `3px solid #FF6B35`, padding: 40, position: "relative", opacity: 0.85,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              border: `1.5px solid ${CREAM}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2.5" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </div>
            <div className="mono" style={{ fontSize: 11, letterSpacing: "0.28em", color: CREAM, textTransform: "uppercase", fontWeight: 700, opacity: 0.9 }}>
              Esto NO es para ti si…
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {FIT_NO.map((f, i) => (
              <div key={i} data-testid={`${testid}-no-${i}`} style={{ display: "flex", gap: 14, alignItems: "flex-start", paddingBottom: i < FIT_NO.length - 1 ? 14 : 0, borderBottom: i < FIT_NO.length - 1 ? `1px solid ${CREAM}0f` : "none" }}>
                <span className="mono" style={{ color: CREAM, opacity: 0.4, fontSize: 12, minWidth: 22 }}>0{i + 1}</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.5, color: CREAM, opacity: 0.65, textDecoration: "line-through", textDecorationColor: `${CREAM}44` }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <style>{`
      @media (max-width: 860px) {
        [data-testid="${testid}"] .fit-grid { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 20. BONUS STACK — "$X in bonuses · totally included" (Maca pattern)
// ============================================================================
const DEFAULT_BONUSES = [
  {
    tag: "BONO 01 · CLASE EN VIVO · 90 MIN",
    title: "Deuda para Fundadores 101",
    line: "El crash-course en vivo de 90 min con el que arranca cada grupo. Incluye desarme de term sheet y Q&A conmigo.",
    val: 497,
  },
  {
    tag: "BONO 02 · WORKBOOK",
    title: "Workbook de Negociación de Term Sheets",
    line: "Un workbook de 42 páginas con las 12 cláusulas que importan — con lenguaje real de 8 facilities distintas.",
    val: 297,
  },
  {
    tag: "BONO 03 · CLASE EN VIVO · 60 MIN",
    title: "Cómo lee tu deck un prestamista de verdad",
    line: "60 minutos dentro de la cabeza de un analista de crédito. Qué buscan, qué los hace rechazarte, cómo llevar el due diligence.",
    val: 397,
  },
  {
    tag: "BONO 04 · PACK DE PLANTILLAS",
    title: "Formato Loan Tape v3.2",
    line: "El formato exacto que +60 fondos quieren ver. Excel + PDF + un walkthrough de 20 min.",
    val: 597,
  },
  {
    tag: "BONO 05 · 3× Q&A EN VIVO",
    title: "Tres office hours en vivo conmigo",
    line: "Sesiones de trabajo de 60 min donde desarmamos tu term sheet, tu loan tape o tu deck. Grabación incluida.",
    val: 897,
  },
];

export const BonusStack = ({
  items = DEFAULT_BONUSES,
  totalValue = 2685,
  testid = "bonus-stack",
}) => (
  <section
    data-testid={testid}
    style={{
      padding: "120px 40px", position: "relative", zIndex: 5,
      background: `linear-gradient(160deg, ${BG_ALT} 0%, ${BG} 100%)`,
      borderTop: `1px solid ${GOLD}22`, borderBottom: `1px solid ${GOLD}22`,
    }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 40, alignItems: "flex-end" }} className="bonus-hdr">
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
            — INCLUIDO EN EL PROGRAMA
          </div>
          <h2 className="serif" style={{
            fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300,
            letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, maxWidth: 900,
            fontVariationSettings: '"SOFT" 30, "opsz" 144',
          }}>
            Cinco bonos. <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>${totalValue.toLocaleString()} USD</span> de valor.
          </h2>
          <p className="serif" style={{ marginTop: 20, fontSize: 18, lineHeight: 1.55, opacity: 0.78, fontStyle: "normal", maxWidth: 640 }}>
            No pagas un dólar extra por ninguno. Vienen con cada lugar del Programa — porque levantar capital no debería ser un deporte solitario.
          </p>
        </div>
        <div
          data-testid={`${testid}-total`}
          style={{
            background: BLUE, color: "#FFFFFF", border: "3px solid #141414",
            borderRadius: 18, boxShadow: "7px 7px 0 #141414",
            padding: "26px 34px", textAlign: "center", position: "relative",
            transform: "rotate(2deg)",
          }}
        >
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: LIME, textTransform: "uppercase", fontWeight: 800 }}>Valor total</div>
          <div className="serif" style={{ fontSize: 58, fontWeight: 300, color: "#FFFFFF", letterSpacing: "-0.035em", lineHeight: 1, marginTop: 8, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
            ${totalValue.toLocaleString()}
          </div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.22em", color: LIME, textTransform: "uppercase", marginTop: 10, fontWeight: 800 }}>
            · incluido gratis ·
          </div>
        </div>
      </div>

      <div style={{ marginTop: 60, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        {items.map((b, i) => (
          <div
            key={i}
            data-testid={`${testid}-item-${i}`}
            style={{
              background: BG, border: "2px solid #141414", borderRadius: 16, boxShadow: "4px 4px 0 #141414", padding: 30, position: "relative",
              transition: "border-color 0.3s ease, transform 0.3s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${CREAM}14`; e.currentTarget.style.transform = "none"; }}
          >
            <div style={{
              position: "absolute", top: -10, right: 20,
              background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414", padding: "4px 10px",
              fontFamily: "'Archivo', sans-serif",
              fontSize: 9, letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase",
            }}>
              Valor ${b.val}
            </div>
            <div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase", opacity: 0.85 }}>
              {b.tag}
            </div>
            <h3 className="serif" style={{
              fontSize: 22, fontWeight: 400, marginTop: 14, letterSpacing: "-0.02em",
              lineHeight: 1.15, color: CREAM, fontVariationSettings: '"SOFT" 30, "opsz" 144',
            }}>
              {b.title}
            </h3>
            <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.55, opacity: 0.75 }}>{b.line}</p>
          </div>
        ))}
      </div>
    </div>
    <style>{`
      @media (max-width: 780px) {
        [data-testid="${testid}"] .bonus-hdr { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 21. CERTIFICATE MOCKUP — visual reward for completing The Playbook
// ============================================================================
export const CertificateMockup = ({
  courseName = "The Capital Playbook",
  studentName = "Tu Nombre Aquí",
  testid = "certificate",
}) => (
  <section
    data-testid={testid}
    style={{ padding: "120px 40px", position: "relative", zIndex: 5 }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 60, alignItems: "center" }} className="cert-grid">
      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
          — TU CREDENCIAL
        </div>
        <h2 className="serif" style={{
          fontSize: "clamp(30px, 4vw, 54px)", fontWeight: 300,
          letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0,
          fontVariationSettings: '"SOFT" 30, "opsz" 144',
        }}>
          Certificado verificable.<br />
          <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>
            Presúmelo en LinkedIn.
          </span>
        </h2>
        <p className="serif" style={{ marginTop: 22, fontSize: 17, lineHeight: 1.55, opacity: 0.82, fontStyle: "normal", maxWidth: 500 }}>
          Termina los 8 módulos y los 5 ejercicios finales y te llevas un certificado digital firmado y verificable — emitido directo por Ernesto Gómez Berjón.
        </p>
        <div style={{ marginTop: 30, display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            "Firmado por Ernesto Gómez Berjón · Managing Partner, ERGO Capital",
            "Hash único en blockchain · verificable por cualquier reclutador",
            "Añádelo a tu perfil de LinkedIn con un click",
            "Aparece en el directorio de alumni (opcional)",
          ].map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ color: GOLD, fontSize: 14, lineHeight: 1.4 }}>✓</span>
              <span style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.85 }}>{b}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Certificate visual — real diploma */}
      <div
        data-testid={`${testid}-visual`}
        style={{ position: "relative", transform: "rotate(-2deg)", transition: "transform 0.5s cubic-bezier(0.16,1,0.3,1)" }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "rotate(0deg) scale(1.02)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "rotate(-2deg)"; }}
      >
        <div className="mono" style={{
          position: "absolute", top: -16, left: -14, zIndex: 3,
          background: LIME, color: "#141414", border: "2px solid #141414", borderRadius: 10,
          boxShadow: "3px 3px 0 #141414", padding: "7px 12px", fontSize: 10,
          letterSpacing: "0.16em", fontWeight: 800, textTransform: "uppercase", transform: "rotate(-8deg)",
        }}>Sello oficial</div>
        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_raise-like-insider/artifacts/boebkkuc_560E6762-420F-4D3C-AC9B-91BDCE1CE0DF.png"
          alt="Certificado The Capital Playbook"
          style={{ width: "100%", height: "auto", display: "block", borderRadius: 10, border: "3px solid #141414", boxShadow: "14px 14px 0 #141414" }}
        />
      </div>
    </div>
    <style>{`
      @media (max-width: 900px) {
        [data-testid="${testid}"] .cert-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 22. PAYMENT METHODS STRIP — LATAM-friendly payment options
// ============================================================================
const PAYMENT_METHODS = [
  { name: "Tarjetas", sub: "Visa · Mastercard · Amex", imgs: ["visa", "mastercard", "americanexpress"] },
  { name: "PayPal", sub: "Global", imgs: ["paypal"] },
  { name: "OXXO", sub: "México · efectivo", word: "OXXO", wordColor: "#E6001F" },
  { name: "Mercado Pago", sub: "MX · AR · BR · CO · CL", imgs: ["mercadopago"] },
  { name: "SPEI", sub: "México · transferencia", word: "SPEI", wordColor: "#3B5EDF" },
  { name: "PIX", sub: "Brasil", imgs: ["pix"] },
  { name: "3 × pagos", sub: "Cuotas sin intereses", word: "3×", wordColor: "#141414" },
  { name: "Transferencia", sub: "USD · global", icon: "bank" },
];

const PayMark = ({ m }) => {
  if (m.imgs) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 26 }}>
        {m.imgs.map((s) => (
          <img key={s} src={`/logos/${s}.svg`} alt={s} style={{ height: 20, width: "auto", display: "block" }} />
        ))}
      </div>
    );
  }
  if (m.word) {
    return (
      <div style={{ height: 26, display: "flex", alignItems: "center" }}>
        <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 18, letterSpacing: "-0.02em", color: m.wordColor }}>{m.word}</span>
      </div>
    );
  }
  return (
    <div style={{ height: 26, display: "flex", alignItems: "center" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#141414" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M4 10h16M12 3l8 5H4l8-5M6 10v8M10 10v8M14 10v8M18 10v8" />
      </svg>
    </div>
  );
};

export const PaymentMethods = ({ testid = "payment-methods" }) => (
  <section
    data-testid={testid}
    style={{ padding: "80px 40px 60px", position: "relative", zIndex: 5, borderTop: `1px solid ${CREAM}0c` }}
  >
    <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 20 }}>
        — PAGO SEGURO · VARIAS OPCIONES
      </div>
      <h3 className="serif" style={{
        fontSize: "clamp(22px, 2.6vw, 32px)", fontWeight: 300,
        letterSpacing: "-0.028em", lineHeight: 1.1, margin: 0, color: CREAM,
        fontVariationSettings: '"SOFT" 30, "opsz" 144',
      }}>
        Paga en tu moneda. <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>Como quieras.</span>
      </h3>

      <div style={{
        marginTop: 40, display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 10, textAlign: "left",
      }}>
        {PAYMENT_METHODS.map((p, i) => (
          <div
            key={i}
            data-testid={`${testid}-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
            style={{
              background: BG_ALT, border: "2px solid #141414", borderRadius: 16, boxShadow: "4px 4px 0 #141414",
              padding: "16px 18px",
              display: "flex", flexDirection: "column", gap: 8,
              transition: "border-color 0.2s ease, background 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.background = BG_ELEV; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${CREAM}14`; e.currentTarget.style.background = BG_ALT; }}
          >
            <PayMark m={p} />
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 13, fontWeight: 600, color: CREAM, letterSpacing: "-0.005em" }}>
              {p.name}
            </div>
            <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: CREAM, opacity: 0.55 }}>
              {p.sub}
            </div>
          </div>
        ))}
      </div>

      <div className="mono" style={{
        marginTop: 30, display: "inline-flex", alignItems: "center", gap: 10,
        fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.55,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        100% seguro · cifrado SSL · procesado por Stripe
      </div>
    </div>
  </section>
);


// ============================================================================
// 23. MEDIA PILLARS — Substack, LinkedIn, YouTube (Maca podcast pattern)
// ============================================================================
const MEDIA_PILLARS = [
  {
    name: "Insider Capital",
    where: "Substack",
    count: "5,400+",
    label: "lectores",
    line: "Operaciones reales de capital, por dentro. Cero relleno.",
    href: "#biblioteca",
    icon: (c) => (
      <svg width="30" height="30" viewBox="0 0 24 24" fill={c}>
        <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
      </svg>
    ),
  },
  {
    name: "Insider Threads",
    where: "LinkedIn",
    count: "18K+",
    label: "seguidores",
    line: "Desarmo deals reales, cambios de mercado y peleas de covenants — en primera persona.",
    href: "https://linkedin.com/in/ernesto-gomez-berjon",
    icon: (c) => (
      <svg width="30" height="30" viewBox="0 0 24 24" fill={c}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: "The Capital Talks",
    where: "YouTube",
    count: "2,100+",
    label: "suscriptores",
    line: "Entrevistas de 45 min con fondos, fundadores y jefes de crédito. A fondo, sin relleno.",
    href: "https://youtube.com",
    icon: (c) => (
      <svg width="30" height="30" viewBox="0 0 24 24" fill={c}>
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
];

export const MediaPillars = ({ testid = "media-pillars" }) => (
  <section
    data-testid={testid}
    style={{ padding: "120px 40px", position: "relative", zIndex: 5, borderTop: `1px solid ${CREAM}0c` }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 40, alignItems: "flex-end", marginBottom: 60 }} className="media-hdr">
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
            — DÓNDE ME ENCUENTRAS
          </div>
          <h2 className="serif" style={{
            fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300,
            letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, maxWidth: 900,
            fontVariationSettings: '"SOFT" 30, "opsz" 144',
          }}>
            Sígueme en <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>tres canales.</span>
          </h2>
        </div>
        <div className="mono" style={{
          fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.55, maxWidth: 260,
        }}>
          — Gratis · público · sin gatekeeping. Si algo es de paga, te lo digo antes.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
        {MEDIA_PILLARS.map((p, i) => (
          <a
            key={i}
            href={p.href}
            data-testid={`${testid}-${i}`}
            className="media-card"
            style={{
              background: BG_ALT, border: "2px solid #141414", borderRadius: 16, boxShadow: "4px 4px 0 #141414",
              padding: 34, textDecoration: "none", color: CREAM,
              display: "flex", flexDirection: "column", gap: 20,
              position: "relative", overflow: "hidden",
              transition: "border-color 0.3s ease, background 0.3s ease, transform 0.3s ease",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{
                width: 56, height: 56, border: `1px solid ${GOLD}44`, background: BG,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {p.icon(GOLD)}
              </div>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: "0.22em", textTransform: "uppercase", color: CREAM, opacity: 0.55, border: `1px solid ${CREAM}18`, padding: "5px 10px" }}>
                {p.where}
              </div>
            </div>
            <div>
              <div className="serif" style={{
                fontSize: 30, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.15, color: CREAM,
                fontVariationSettings: '"SOFT" 30, "opsz" 144',
              }}>
                {p.name}
              </div>
              <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.55, opacity: 0.72 }}>{p.line}</p>
            </div>
            <div className="mono" style={{
              display: "inline-flex", gap: 10, alignItems: "center",
              fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD,
              marginTop: 6,
            }}>
              Seguir → <span className="media-arrow" style={{ transition: "transform 0.3s ease" }}>·</span>
            </div>
          </a>
        ))}
      </div>
    </div>
    <style>{`
      .media-card:hover { border-color: ${GOLD} !important; background: ${BG_ELEV} !important; transform: translateY(-4px); }
      .media-card:hover .media-arrow { transform: translateX(8px); }
      @media (max-width: 780px) {
        [data-testid="${testid}"] .media-hdr { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </section>
);


// ============================================================================
// 24. CURRICULUM ACCORDION — expandable weekly breakdown for The Program
// ============================================================================
const PROGRAM_WEEKS = [
  {
    n: "SEMANA 01",
    title: "Diagnosticar · Tu estructura de capital hoy",
    duration: "4-6 hrs",
    lessons: [
      "Kickoff en vivo · 90 min conmigo",
      "Auditoría de cap table + revisión de waterfall",
      "Calculadora de tu costo de capital actual",
      "Scorecard de readiness · 6 dimensiones",
      "Tarea: entrega tu primer borrador de memo",
    ],
  },
  {
    n: "SEMANA 02",
    title: "Elegir · El instrumento correcto para tu etapa",
    duration: "4-6 hrs",
    lessons: [
      "Las 5 fuentes de capital · a fondo",
      "Equity vs deuda vs venture debt · árbol de decisión",
      "SAFE, convertible, warehouse, mezz · cuándo gana cada uno",
      "Caso en vivo · un raise de $3M que estructuramos el trimestre pasado",
      "Tarea: elige tu estructura objetivo y defiéndela",
    ],
  },
  {
    n: "SEMANA 03-04",
    title: "Estructurar · Plantillas de term sheet y peleas de covenants",
    duration: "8-12 hrs",
    lessons: [
      "3 plantillas de term sheet pre-negociadas",
      "Taxonomía de covenants · redlineada de 8 facilities",
      "Las 12 cláusulas que de verdad importan",
      "Drill en vivo · redlinea un term sheet real conmigo",
      "Tarea: arma tu term sheet v0",
    ],
  },
  {
    n: "SEMANA 05-06",
    title: "Empaquetar · Loan tape + data room + pitch",
    duration: "8-12 hrs",
    lessons: [
      "Formato loan tape v3.2 · el estándar de +60 fondos",
      "Armado del data room · qué va dónde y por qué",
      "El pitch de 45 slides · ni una más",
      "Las 12 preguntas que te van a hacer los fondos · textuales",
      "Tarea: entrega tu data room para revisión de pares y mía",
    ],
  },
  {
    n: "SEMANA 07-08",
    title: "Distribuir · Warm intros a +60 prestamistas",
    duration: "6-10 hrs",
    lessons: [
      "El directorio de prestamistas · segmentado por tesis y ticket",
      "El framework de warm intro · script + timing",
      "Sesión de warm intro en vivo · te presento a 3-5 targets",
      "Cadence de follow-up · qué decir y cuándo",
      "Tarea: corre tus primeras 5 llamadas de intro",
    ],
  },
  {
    n: "SEMANA 09-10",
    title: "Cerrar · Due diligence, negociación, primer cierre",
    duration: "6-10 hrs",
    lessons: [
      "Manejo del Q&A de due diligence · playbook + plantillas",
      "El baile de la negociación · redlines, deadlines, cuándo pararte",
      "Checklist de cierre · legal, ops, comunicación",
      "Post-cierre · reporting, cumplimiento de covenants, siguiente raise",
      "Proyecto final: tu facility cerrada / aprobada por comité",
    ],
  },
];

export const CurriculumAccordion = ({ testid = "curriculum" }) => {
  const [open, setOpen] = useState(0);
  return (
    <section
      data-testid={testid}
      style={{ padding: "120px 40px", position: "relative", zIndex: 5, background: BG_ALT, borderTop: `1px solid ${CREAM}0c` }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>
          — POR DENTRO DEL PROGRAMA · 10 SEMANAS
        </div>
        <h2 className="serif" style={{
          fontSize: "clamp(32px, 4vw, 60px)", fontWeight: 300,
          letterSpacing: "-0.035em", lineHeight: 1.02, margin: 0, maxWidth: 1000,
          fontVariationSettings: '"SOFT" 30, "opsz" 144',
        }}>
          Diez semanas. Seis fases. <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>Un primer cierre.</span>
        </h2>
        <p className="serif" style={{ marginTop: 20, fontSize: 18, lineHeight: 1.55, opacity: 0.75, fontStyle: "normal", maxWidth: 720 }}>
          Cada semana está diseñada para acercarte un paso a tu facility firmado. Haz click en cualquier fase para ver las lecciones y la tarea.
        </p>

        <div style={{ marginTop: 50, display: "flex", flexDirection: "column", gap: 8 }}>
          {PROGRAM_WEEKS.map((w, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                data-testid={`${testid}-week-${i}`}
                style={{
                  border: `1px solid ${isOpen ? GOLD : CREAM + "14"}`,
                  background: isOpen ? BG : BG,
                  transition: "border-color 0.3s ease",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`${testid}-week-${i}-toggle`}
                  style={{
                    width: "100%", textAlign: "left", padding: "24px 32px",
                    background: "transparent", border: "none", color: CREAM,
                    cursor: "pointer", display: "grid",
                    gridTemplateColumns: "auto 1fr auto auto",
                    gap: 24, alignItems: "center",
                  }}
                  className="curriculum-toggle"
                >
                  <div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", color: GOLD, textTransform: "uppercase", opacity: 0.85, fontWeight: 700, minWidth: 90 }}>
                    {w.n}
                  </div>
                  <div className="serif" style={{
                    fontSize: "clamp(18px, 2.2vw, 24px)", fontWeight: 400,
                    letterSpacing: "-0.015em", lineHeight: 1.2, color: CREAM,
                    fontVariationSettings: '"SOFT" 30, "opsz" 144',
                  }}>
                    {w.title}
                  </div>
                  <div className="mono curriculum-duration" style={{
                    fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                    color: CREAM, opacity: 0.55, border: `1px solid ${CREAM}18`,
                    padding: "4px 10px", whiteSpace: "nowrap",
                  }}>
                    {w.duration}
                  </div>
                  <div style={{
                    width: 30, height: 30, border: `1px solid ${isOpen ? GOLD : CREAM + "22"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "transform 0.3s ease",
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isOpen ? GOLD : CREAM} strokeWidth="1.5">
                      <line x1="12" y1="4" x2="12" y2="20" />
                      <line x1="4" y1="12" x2="20" y2="12" />
                    </svg>
                  </div>
                </button>
                <div
                  data-testid={`${testid}-week-${i}-panel`}
                  style={{
                    maxHeight: isOpen ? 500 : 0, overflow: "hidden",
                    transition: "max-height 0.4s cubic-bezier(0.16,1,0.3,1)",
                  }}
                >
                  <div style={{
                    padding: "0 32px 28px 122px",
                    borderTop: isOpen ? `1px solid ${CREAM}0f` : "none",
                    display: "flex", flexDirection: "column", gap: 12,
                  }} className="curriculum-panel-inner">
                    <div style={{ marginTop: 22 }}>
                      {w.lessons.map((l, j) => (
                        <div key={j} className="mono" style={{
                          fontSize: 12.5, letterSpacing: "0.04em", lineHeight: 1.5,
                          color: CREAM, opacity: 0.82,
                          display: "flex", gap: 12, alignItems: "baseline",
                          padding: "8px 0",
                          borderBottom: j < w.lessons.length - 1 ? `1px solid ${CREAM}08` : "none",
                        }}>
                          <span style={{ color: GOLD, opacity: 0.7, minWidth: 26 }}>0{j + 1}</span>
                          <span>{l}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 50, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <a href="#pricing" data-testid={`${testid}-cta`} className="mono" style={{
            padding: "16px 26px", background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414",
            fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 700,
            textDecoration: "none",
          }}>
            Ver precios e inscribirme →
          </a>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.55 }}>
            10 semanas · 4-6 hrs/semana · $7,500 · 12 lugares
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 780px) {
          [data-testid="${testid}"] .curriculum-toggle {
            grid-template-columns: auto 1fr auto !important;
            gap: 14px !important; padding: 18px 20px !important;
          }
          [data-testid="${testid}"] .curriculum-duration { display: none; }
          [data-testid="${testid}"] .curriculum-panel-inner { padding: 0 20px 22px 20px !important; }
        }
      `}</style>
    </section>
  );
};


// ============================================================================
// 25. PERSONAL NOTE — Ernesto's first-person warm-cream letter (Maca warmth)
// ============================================================================
export const PersonalNote = ({ testid = "personal-note" }) => (
  <section
    data-testid={testid}
    style={{
      padding: "120px 40px", position: "relative", zIndex: 5,
      background: "#3B5EDF",
      color: "#FFFFFF",
    }}
  >
    {/* Decorative lime shapes · Maca-style blobs */}
    <svg
      width="180" height="180" viewBox="0 0 180 180"
      style={{ position: "absolute", top: -30, right: -30, opacity: 0.9, zIndex: 0 }}
      aria-hidden="true"
    >
      <circle cx="120" cy="60" r="46" fill="#D9F154" opacity="0.35" />
    </svg>
    <svg
      width="160" height="160" viewBox="0 0 140 140"
      style={{ position: "absolute", bottom: -24, left: -24, opacity: 0.85, zIndex: 0 }}
      aria-hidden="true"
    >
      <path d="M 20 70 Q 70 10 120 70 Q 70 130 20 70 Z" fill="#D9F154" opacity="0.28" />
    </svg>

    <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
      <div className="mono" style={{
        fontSize: 10, letterSpacing: "0.28em", color: "#D9F154",
        textTransform: "uppercase", marginBottom: 22, fontWeight: 700,
      }}>
        — UNA NOTA DE ERNESTO
      </div>

      <h2 className="serif" style={{
        fontSize: "clamp(32px, 4.4vw, 60px)",
        letterSpacing: "-0.038em", lineHeight: 1.05, margin: 0, color: "#FFFFFF",
      }}>
        Hice esto porque yo lo <span style={{ color: "#D9F154" }}>necesitaba</span> y no existía.
      </h2>

      <div style={{
        marginTop: 40, fontSize: 19, lineHeight: 1.7,
        color: "rgba(255,255,255,0.92)", maxWidth: 720,
      }}>
        <p style={{ margin: "0 0 24px" }}>
          En 2015 firmé mi primer term sheet. Fue malísimo — 22% de tasa, un aval personal que no debí dar, y un paquete de covenants que nos habría tumbado al primer trimestre flojo.
        </p>
        <p style={{ margin: "0 0 24px" }}>
          Nadie me advirtió. No había bootcamp, ni playbook, ni un grupo de gente que ya lo hubiera hecho antes que yo. Solo un abogado que me cobró $30K por revisar un documento que él tampoco entendía bien.
        </p>
        <p style={{ margin: "0 0 24px" }}>
          Diez años después llevo +1.2B USD en term sheets firmados, corrí un fondo de crédito, asesoré a varias fintechs y rechacé más deals de los que cerré. Y lo que más escucho de los fundadores es: <span style={{ color: "#D9F154", fontWeight: 600 }}>"ojalá alguien me hubiera dicho esto antes de firmar".</span>
        </p>
        <p style={{ margin: "0 0 24px" }}>
          Eso es The Debt Guy. No soy consultor ni gatekeeper — soy un operador que estuvo de los dos lados de la mesa y escribió lo que sí funciona.
        </p>
        <p style={{ margin: "0 0 8px" }}>
          Si ahorita estás viendo tu primer term sheet, ojalá te sumes. Y si no — <a href="#biblioteca" style={{ color: "#D9F154", textDecoration: "underline", textDecorationThickness: "1px" }}>descarga un ebook gratis</a>. Sin costo, sin letras chiquitas.
        </p>
      </div>

      {/* Signature */}
      <div style={{
        marginTop: 40, paddingTop: 30,
        borderTop: "1px solid rgba(217,241,84,0.4)",
        display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
      }}>
        <div className="serif" style={{
          fontSize: 34, color: "#FFFFFF",
          letterSpacing: "-0.02em", lineHeight: 1,
        }}>
          — Ernesto
        </div>
        <div className="mono" style={{
          fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase",
          color: "rgba(255,255,255,0.7)",
        }}>
          Managing Partner · ERGO Capital
        </div>
      </div>
    </div>
  </section>
);


// ============================================================================
// 26. DECORATIVE MOTIFS — subtle line-art background pattern
// ============================================================================
export const DecorativeMotifs = () => (
  <div aria-hidden="true" style={{
    position: "absolute", inset: 0, pointerEvents: "none",
    overflow: "hidden", zIndex: 0,
  }}>
    <svg style={{ position: "absolute", top: "8%", right: "5%", opacity: 0.06 }} width="120" height="120" viewBox="0 0 120 120" fill="none" stroke={GOLD} strokeWidth="1">
      <rect x="30" y="40" width="10" height="60" />
      <rect x="48" y="20" width="10" height="80" />
      <rect x="66" y="55" width="10" height="45" />
      <rect x="84" y="35" width="10" height="65" />
    </svg>
    <svg style={{ position: "absolute", bottom: "10%", left: "3%", opacity: 0.06 }} width="140" height="90" viewBox="0 0 140 90" fill="none" stroke={GOLD} strokeWidth="1">
      <path d="M 5 70 L 25 55 L 45 60 L 65 40 L 85 45 L 105 25 L 125 30" />
      <circle cx="25" cy="55" r="2" />
      <circle cx="65" cy="40" r="2" />
      <circle cx="105" cy="25" r="2" />
    </svg>
    <svg style={{ position: "absolute", top: "45%", left: "8%", opacity: 0.05 }} width="80" height="80" viewBox="0 0 80 80" fill="none" stroke={GOLD} strokeWidth="1">
      <circle cx="40" cy="40" r="34" />
      <path d="M 40 6 L 40 74 M 6 40 L 74 40" />
    </svg>
  </div>
);
